import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, computed, signal, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, concatMap, debounceTime, forkJoin, from, map, of, Subject, Subscription } from 'rxjs';

import { BondDetailService } from './bond-detail.service';
import { InvestorService } from '../../catalog/investor/investor.service';
import { CreateInvestorRequest, Investor, InvestorGroupOption, InvestorTypeOption } from '../../catalog/investor/investor.types';
import { InvestorGroupService } from '../../catalog/investor-group/investor-group.service';
import { CreateInvestorGroupRequest } from '../../catalog/investor-group/investor-group.types';
import { CatalogLocationService, CountryOption, RegionOption } from '../../catalog/shared/catalog-location.service';
import { ReofferBmkService } from '../../catalog/reoffer-bmk/reoffer-bmk.service';
import { ReofferBmk } from '../../catalog/reoffer-bmk/reoffer-bmk.types';
import { NonNamedDealerService } from '../../catalog/non-named-dealer/non-named-dealer.service';
import { NonNamedDealer } from '../../catalog/non-named-dealer/non-named-dealer.types';
import { CatalogCrudModalComponent } from '../../catalog/shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../../catalog/shared/components/catalog-delete-confirm-modal';
import { CatalogModalController } from '../../catalog/shared/catalog-modal.controller';
import { DatatableController } from '../../shared/datatable.controller';
import {
  BondDetail as BondDetailModel,
  BondDetailUpsertRequest,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  BondDocumentItem,
  BondInvestorItem,
  BondNoteItem,
  BondTradeBmkItem,
  RelatedInstrumentItem,
  DwCustomerGroup,
  DwCustomerItem,
  NoteCreateRequest,
  NoteResponse,
  NonNamedDealerItem,
  SimpleCatalogOption,
  TradeLeadItem,
} from './bond-detail.types';
import { resolveApiErrorMessage } from '../../shared/api-error';
import {
  buildDocumentUploadToast,
  createDocumentUploadEntries,
  DocumentUploadEntry,
  summarizeDocumentUploads,
  updateDocumentUploadEntry,
  uploadDocumentsSequentially,
} from '../../shared/document-upload';
import { DocumentUploadList } from '../../shared/document-upload-list';
import { DocumentNameCell } from '../../shared/document-name-cell';
import { DocumentDescriptionCell } from '../../shared/document-description-cell';
import { BondPricingSupplement } from './pricing-supplement/bond-pricing-supplement';

interface BondDetailSection {
  id: string;
  label: string;
  panelId: string;
  iconClass: string;
}

type BondDetailSectionId =
  | 'bond-setup'
  | 'pricing-supplements'
  | 'legacy'
  | 'bond-fees'
  | 'investors-allocations'
  | 'leads-co-leads'
  | 'related-instruments'
  | 'non-named-dealers'
  | 'documentation'
  | 'notes';

interface KTTabsInstance {
  show: (tabElement: HTMLElement) => void;
  dispose?: () => void;
}

interface KTTabsConstructor {
  createInstances?: () => void;
  getOrCreateInstance?: (element: HTMLElement) => KTTabsInstance;
}

interface AddBmkFormValue {
  value: string;
}

const DEFAULT_CUSTOMER_PARENT = 'Parent Bank';

interface AddLeadFormValue {
  customerId: string;
  customerName: string;
  role: string;
  arbitrageNotional: string;
  feeLeadColead: string;
  useCustomFee: boolean;
  feePercentage: string;
}

interface LeadCustomerSelection {
  id: string;
  legalName: string;
}

interface InlineLeadDraft {
  role: string;
  arbitrageNotional: string;
}

interface AddDocumentFormValue {
  description: string;
  filename: string;
  files: File[];
}

interface AddNoteFormValue {
  note: string;
}

type RepurcNumericField =
  | 'bondGlRepurchase'
  | 'bondGlRefinance'
  | 'us3MlCost'
  | 'repurcOriginalCost'
  | 'repurcNewFundingCost';

interface InvestorModalRow extends Investor {
  isAdded: boolean;
  currentAllocation: number | null;
}

type InvestorsModalTab = 'select-allocate' | 'create-investor' | 'create-investor-group';

interface CreateInvestorInlineFormValue {
  name: string;
  investorTypeId: string;
  countryId: string;
  regionId: string;
  investorGroupId: string;
}

interface CreateInvestorGroupInlineFormValue {
  name: string;
  hqCountryId: string;
  regionId: string;
}

type SaveIntent = 'save' | 'save-and-close';

interface PendingItemFlags {
  type: boolean;
  fundingCost: boolean;
  tranche: boolean;
  legalDocument: boolean;
  termSheet: boolean;
  rta: boolean;
  investors: boolean;
  leads: boolean;
}

export interface PendingLeadRow {
  customerId: string;
  customerName: string;
  role: string;
  saving: boolean;
  error: string;
}

@Component({
  selector: 'app-bond-detail',
  imports: [
    CommonModule,
    RouterLink,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
    DocumentUploadList,
    DocumentNameCell,
    DocumentDescriptionCell,
    BondPricingSupplement,
  ],
  templateUrl: './bond-detail.html',
  styleUrl: './bond-detail.css',
})
export class BondDetail implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabsRoot')
  private tabsRoot?: ElementRef<HTMLElement>;

  @ViewChild('documentFilesInput')
  private documentFilesInput?: ElementRef<HTMLInputElement>;

  private tabsInstance: KTTabsInstance | null = null;
  private detailSubscription?: Subscription;
  private saveSubscription?: Subscription;
  private initializeSubscription?: Subscription;
  private availableInvestorsSubscription?: Subscription;
  private availableReofferBmksSubscription?: Subscription;
  private groupedCustomersSubscription?: Subscription;
  private customerSearchSubscription?: Subscription;
  private pendingLeadsSaveSubscription?: Subscription;
  private readonly customerSearchInput = new Subject<string>();
  private relatedInstrumentsSubscription?: Subscription;
  private investorCreationLookupSubscription?: Subscription;
  private createInvestorSubscription?: Subscription;
  private createInvestorGroupSubscription?: Subscription;
  private notesSubscription?: Subscription;
  private documentsSubscription?: Subscription;
  private noteMutationSubscription?: Subscription;
  private leadMutationSubscription?: Subscription;
  private nonNamedDealersSubscription?: Subscription;
  private availableNonNamedDealersSubscription?: Subscription;
  private documentMutationSubscription?: Subscription;
  private addDocumentModalCloseObserver?: MutationObserver;
  private addInvestorsModalCloseObserver?: MutationObserver;
  private readonly modalController = new CatalogModalController();
  private readonly saveModalId = 'bond_detail_save_modal';
  private readonly addBmkModalId = 'bond_detail_add_bmk_modal';
  private readonly addInvestorsModalId = 'bond_detail_add_investors_modal';
  private readonly addLeadModalId = 'bond_detail_add_lead_modal';
  private readonly nonNamedDealersModalId = 'bond_detail_non_named_dealers_modal';
  private readonly addDocumentModalId = 'bond_detail_add_document_modal';
  private readonly deleteDocumentModalId = 'bond_detail_delete_document_modal';
  private readonly addNoteModalId = 'bond_detail_add_note_modal';
  private readonly deleteModalId = 'bond_detail_delete_modal';
  private readonly deleteBmkModalId = 'bond_detail_delete_bmk_modal';
  private readonly deleteInvestorModalId = 'bond_detail_delete_investor_modal';
  private readonly deleteNoteModalId = 'bond_detail_delete_note_modal';
  private readonly discardChangesModalId = 'bond_detail_discard_changes_modal';
  private readonly notesSource = 'Bond';
  private readonly documentSource = 'Bond';
  private readonly allowedDocumentTypes = ['.doc', '.msg', '.pdf', '.xls'];
  private readonly relatedInstrumentsDatatableController = new DatatableController('kt_related_instruments_datatable');

  protected readonly tradeId: string;
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly initializeError = signal('');
  protected readonly isInitializing = signal(false);
  protected readonly hasUnsavedChanges = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal('');
  protected readonly bpsPerYear = signal('');
  protected readonly saveIntent = signal<SaveIntent>('save');
  protected readonly addFormError = signal('');
  protected readonly addInvestorsError = signal('');
  protected readonly isMutatingNote = signal(false);
  protected readonly isMutatingInvestor = signal(false);
  protected readonly saveInfo = signal('');
  protected readonly editingBmkId = signal<number | null>(null);
  protected readonly isMutatingBmk = signal(false);
  protected readonly editingLeadRowKey = signal<string | null>(null);
  protected readonly editingDocumentId = signal<number | null>(null);
  protected readonly editingNoteId = signal<number | null>(null);
  protected readonly isMutatingDocument = signal(false);
  protected readonly renamingDocumentId = signal<number | null>(null);
  protected readonly describingDocumentId = signal<number | null>(null);
  protected readonly documentUploadEntries = signal<DocumentUploadEntry[]>([]);
  protected readonly isLoadingAvailableInvestors = signal(false);
  protected readonly isLoadingAvailableReofferBmks = signal(false);
  protected readonly isLoadingGroupedCustomers = signal(false);
  protected readonly isMutatingLead = signal(false);
  protected readonly isLoadingRelatedInstruments = signal(false);
  protected readonly relatedInstrumentsError = signal('');
  protected readonly availableReofferBmks = signal<ReofferBmk[]>([]);
  protected readonly groupedCustomers = signal<DwCustomerGroup[]>([]);
  protected readonly customerSearchTerm = signal('');
  protected readonly editingLeadCustomerId = signal<string | null>(null);
  protected readonly selectedLeadCustomers = signal<LeadCustomerSelection[]>([]);
  protected readonly expandedCustomerGroups = signal<ReadonlySet<string>>(new Set<string>());
  protected readonly appliedCustomerSearchTerm = signal('');
  protected readonly inlineEditingLeadCustomerId = signal<string | null>(null);
  protected readonly inlineLeadDraft = signal<InlineLeadDraft>({ role: '', arbitrageNotional: '' });
  protected readonly pendingLeads = signal<PendingLeadRow[]>([]);
  protected readonly isSavingPendingLeads = signal(false);
  protected readonly pendingLeadsReadyCount = computed(() =>
    this.pendingLeads().filter((row) => row.role.trim() !== '').length,
  );
  protected readonly availableNonNamedDealers = signal<NonNamedDealer[]>([]);
  protected readonly isLoadingNonNamedDealers = signal(false);
  protected readonly isSavingNonNamedDealers = signal(false);
  protected readonly nonNamedDealerFormError = signal('');
  protected readonly nonNamedDealerSearchTerm = signal('');
  protected readonly selectedNonNamedDealerIds = signal<ReadonlySet<number>>(new Set<number>());
  protected readonly filteredNonNamedDealers = computed(() => {
    const term = this.nonNamedDealerSearchTerm().trim().toLowerCase();
    return this.availableNonNamedDealers()
      .filter((dealer) => !term || dealer.name.toLowerCase().includes(term))
      .sort((left, right) => left.name.localeCompare(right.name));
  });
  protected readonly reofferBmkSearchTerm = signal('');
  protected readonly selectedReofferBmkId = signal<number | null>(null);
  protected readonly selectedReofferBmkName = signal('');
  protected readonly pendingBmkDeleteId = signal<number | null>(null);
  protected readonly pendingBmkDeleteName = signal('');
  protected readonly availableInvestors = signal<Investor[]>([]);
  protected readonly investorModalSearchTerm = signal('');
  protected readonly investorModalPageIndex = signal(1);
  protected readonly investorModalPageSize = signal(10);
  protected readonly investorModalActiveTab = signal<InvestorsModalTab>('select-allocate');
  protected readonly isLoadingInvestorCreateLookups = signal(false);
  protected readonly investorCreateLookupError = signal('');
  protected readonly isCreatingInvestor = signal(false);
  protected readonly isCreatingInvestorGroup = signal(false);
  protected readonly createInvestorError = signal('');
  protected readonly createInvestorGroupError = signal('');
  protected readonly investorModalInvestorTypes = signal<InvestorTypeOption[]>([]);
  protected readonly investorModalInvestorGroups = signal<InvestorGroupOption[]>([]);
  protected readonly investorModalCountries = signal<CountryOption[]>([]);
  protected readonly investorModalRegions = signal<RegionOption[]>([]);
  protected readonly editingInvestorAllocationId = signal<number | null>(null);
  protected readonly editingInvestorAllocationValue = signal('');
  protected readonly editingMainInvestorAllocationId = signal<number | null>(null);
  protected readonly editingMainInvestorAllocationValue = signal('');
  protected readonly pendingInvestorDeleteId = signal<number | null>(null);
  protected readonly pendingInvestorDeleteName = signal('');
  protected readonly pendingNoteDeleteId = signal<number | null>(null);
  protected readonly pendingNoteDeletePreview = signal('');
  protected readonly pendingDocumentDeleteId = signal<number | null>(null);
  protected readonly pendingDocumentDeletePreview = signal('');
  protected readonly autoUs3MlCostChecked = signal(false);
  protected readonly autoSofrCostChecked = signal(false);
  protected readonly repurcOriginalCostBeforeAuto = signal<number | null>(null);
  protected readonly isDeletingBond = signal(false);
  protected readonly investorModalPageSizeOptions = [10, 20, 50];
  protected readonly addBmkForm = signal<AddBmkFormValue>({
    value: '',
  });
  protected readonly addLeadForm = signal<AddLeadFormValue>({
    customerId: '',
    customerName: '',
    role: 'LEAD',
    arbitrageNotional: '',
    feeLeadColead: '',
    useCustomFee: false,
    feePercentage: '',
  });
  protected readonly addDocumentForm = signal<AddDocumentFormValue>({
    description: '',
    filename: '',
    files: [],
  });
  protected readonly addNoteForm = signal<AddNoteFormValue>({
    note: '',
  });
  protected readonly createInvestorForm = signal<CreateInvestorInlineFormValue>({
    name: '',
    investorTypeId: '',
    countryId: '',
    regionId: '',
    investorGroupId: '',
  });
  protected readonly createInvestorGroupForm = signal<CreateInvestorGroupInlineFormValue>({
    name: '',
    hqCountryId: '',
    regionId: '',
  });
  protected readonly detail = signal<BondDetailModel | null>(null);
  protected readonly sections: BondDetailSection[] = [
    {
      id: 'bond-setup',
      label: 'Main Details',
      panelId: 'bond-detail-tab-bond-setup',
      iconClass: 'ki-setting-2',
    },
    {
      id: 'bond-fees',
      label: 'Bond Fees',
      panelId: 'bond-detail-tab-bond-fees',
      iconClass: 'ki-dollar',
    },
    {
      id: 'investors-allocations',
      label: 'Investors & Allocations',
      panelId: 'bond-detail-tab-investors-allocations',
      iconClass: 'ki-bank',
    },
    {
      id: 'leads-co-leads',
      label: 'Leads / Co-Leads',
      panelId: 'bond-detail-tab-leads-co-leads',
      iconClass: 'ki-people',
    },
    {
      id: 'legacy',
      label: 'Legacy',
      panelId: 'bond-detail-tab-legacy',
      iconClass: 'ki-time',
    },
    {
      id: 'related-instruments',
      label: 'Related Instruments',
      panelId: 'bond-detail-tab-related-instruments',
      iconClass: 'ki-arrows-circle',
    },
    {
      id: 'documentation',
      label: 'Documentation',
      panelId: 'bond-detail-tab-documentation',
      iconClass: 'ki-file-up',
    },
    {
      id: 'pricing-supplements',
      label: 'Upload to Public Website',
      panelId: 'bond-detail-tab-pricing-supplements',
      iconClass: 'ki-cloud-add',
    },
    {
      id: 'non-named-dealers',
      label: 'Non Named Dealers',
      panelId: 'bond-detail-tab-non-named-dealers',
      iconClass: 'ki-bank',
    },
    {
      id: 'notes',
      label: 'Notes',
      panelId: 'bond-detail-tab-notes',
      iconClass: 'ki-notepad',
    },
  ];
  protected readonly visibleSections = computed(() => {
    return this.sections.filter((section) => this.isSectionVisible(section.id as BondDetailSectionId));
  });
  // Bumped after each successful save so the Upload to Public Website tab re-checks the required bond fields.
  protected readonly pricingSupplementReloadKey = signal(0);
  // Mirrors the backend rules in PendingBondService so users can see why a bond is listed in Pending Items.
  protected readonly pendingItemFlags = computed<PendingItemFlags>(() => {
    const detail = this.detail();
    const none: PendingItemFlags = {
      type: false, fundingCost: false, tranche: false, legalDocument: false, termSheet: false, rta: false, investors: false, leads: false,
    };
    if (!detail) {
      return none;
    }

    const marketType = detail.marketType.trim().toUpperCase();
    const securityType = detail.securityType.trim().toUpperCase();
    const isCdn = securityType === 'CDN';
    const isRepurc = marketType === 'REPURC';
    const isInitReopenBond = (marketType === 'INIT' || marketType === 'REOPEN')
      && (securityType === 'BOND' || securityType === 'FRN');
    const allocationDelta = this.investorAllocationDelta();

    return {
      type: isInitReopenBond && detail.isSale && detail.bondTypeId === null,
      fundingCost: isInitReopenBond && detail.isSale && detail.sofrCost === null && detail.us3MlCost === null,
      tranche: !isCdn && !isRepurc && detail.isSale && detail.tranche.trim() === '',
      legalDocument: !isCdn && !isRepurc && !detail.hasLegalDocument,
      termSheet: !isCdn && !detail.hasTermSheet,
      rta: !isCdn && !detail.hasRta,
      investors: (isInitReopenBond || isCdn) && (allocationDelta === null || Math.abs(allocationDelta) > 1e-6),
      leads: isInitReopenBond
        && !detail.tradeLead.some((lead) => this.normalizeUiLeadRole(lead.role) === 'LEAD'),
    };
  });
  protected readonly pendingSectionReasons = computed<Partial<Record<BondDetailSectionId, string[]>>>(() => {
    const flags = this.pendingItemFlags();
    const reasons: Partial<Record<BondDetailSectionId, string[]>> = {};
    const add = (section: BondDetailSectionId, active: boolean, reason: string): void => {
      if (active) {
        (reasons[section] ??= []).push(reason);
      }
    };

    add('bond-setup', flags.type, 'Type is missing');
    add('bond-setup', flags.fundingCost, 'Funding cost is missing (SOFR or 3mL)');
    add('bond-setup', flags.tranche, 'Tranche is missing');
    add('documentation', flags.legalDocument, 'Legal Document is not checked');
    add('documentation', flags.termSheet, 'Term Sheet is not checked');
    add('documentation', flags.rta, 'RTA is not checked');
    add('investors-allocations', flags.investors, 'Investor allocation does not match the Notional Issued');
    add('leads-co-leads', flags.leads, 'No saved lead with the LEAD role');
    return reasons;
  });

  protected sectionPendingReasons(sectionId: string): string[] {
    return this.pendingSectionReasons()[sectionId as BondDetailSectionId] ?? [];
  }
  protected readonly investorModalRows = computed<InvestorModalRow[]>(() => {
    const investorAllocations = new Map(
      (this.detail()?.investors ?? []).map((investor) => [investor.id, investor.allocation ?? null]),
    );

    return this.availableInvestors().map((investor) => ({
      ...investor,
      isAdded: investorAllocations.has(investor.id),
      currentAllocation: investorAllocations.get(investor.id) ?? null,
    }));
  });
  protected readonly filteredInvestorModalRows = computed<InvestorModalRow[]>(() => {
    const searchTerm = this.investorModalSearchTerm().trim().toLowerCase();
    if (!searchTerm) {
      return this.investorModalRows();
    }

    return this.investorModalRows().filter((investor) => {
      const searchableValue = [
        investor.name,
        investor.investorGroupName,
        investor.investorTypeName,
        investor.region,
        investor.country,
      ].join(' ').toLowerCase();

      return searchableValue.includes(searchTerm);
    });
  });
  protected readonly investorModalTotalPages = computed(() => {
    const pageSize = this.investorModalPageSize();
    const totalRows = this.filteredInvestorModalRows().length;
    return Math.max(1, Math.ceil(totalRows / pageSize));
  });
  protected readonly investorModalCurrentPage = computed(() => {
    const requestedPage = this.investorModalPageIndex();
    const totalPages = this.investorModalTotalPages();
    return Math.min(Math.max(requestedPage, 1), totalPages);
  });
  protected readonly pagedInvestorModalRows = computed<InvestorModalRow[]>(() => {
    const pageSize = this.investorModalPageSize();
    const page = this.investorModalCurrentPage();
    const start = (page - 1) * pageSize;
    return this.filteredInvestorModalRows().slice(start, start + pageSize);
  });
  protected readonly investorModalRangeStart = computed(() => {
    const totalRows = this.filteredInvestorModalRows().length;
    if (totalRows === 0) {
      return 0;
    }

    return (this.investorModalCurrentPage() - 1) * this.investorModalPageSize() + 1;
  });
  protected readonly investorModalRangeEnd = computed(() => {
    const totalRows = this.filteredInvestorModalRows().length;
    if (totalRows === 0) {
      return 0;
    }

    return Math.min(this.investorModalCurrentPage() * this.investorModalPageSize(), totalRows);
  });
  protected readonly filteredReofferBmks = computed<ReofferBmk[]>(() => {
    const searchTerm = this.reofferBmkSearchTerm().trim().toLowerCase();
    if (!searchTerm) {
      return this.availableReofferBmks();
    }

    return this.availableReofferBmks().filter((reoffer) => {
      return reoffer.name.toLowerCase().includes(searchTerm);
    });
  });


  constructor(
    private readonly route: ActivatedRoute,
    private readonly bondDetailService: BondDetailService,
    private readonly investorService: InvestorService,
    private readonly investorGroupService: InvestorGroupService,
    private readonly catalogLocationService: CatalogLocationService,
    private readonly reofferBmkService: ReofferBmkService,
    private readonly nonNamedDealerService: NonNamedDealerService,
    private readonly titleService: Title,
    private readonly router: Router,
  ) {
    this.tradeId = this.route.snapshot.paramMap.get('tradeId') ?? '';
  }

  @HostListener('window:beforeunload', ['$event'])
  protected handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges() && this.pendingLeads().length === 0) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  ngOnInit(): void {
    this.updateDocumentTitle();
    this.loadDetail();
    this.customerSearchSubscription = this.customerSearchInput
      .pipe(debounceTime(350), map((term) => term.trim()))
      .subscribe((term) => {
        if (term !== this.appliedCustomerSearchTerm()) {
          this.loadGroupedCustomers(term);
        }
      });
  }

  ngAfterViewInit(): void {
    this.initializeKTTabs();
    this.addDocumentModalCloseObserver = this.modalController.observeClose(this.addDocumentModalId, () => {
      this.resetAddDocumentForm();
    });
    this.addInvestorsModalCloseObserver = this.modalController.observeClose(this.addInvestorsModalId, () => {
      this.resetAddInvestorsModal();
    });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle('FRIDA');
    this.detailSubscription?.unsubscribe();
    this.saveSubscription?.unsubscribe();
    this.initializeSubscription?.unsubscribe();
    this.availableInvestorsSubscription?.unsubscribe();
    this.availableReofferBmksSubscription?.unsubscribe();
    this.groupedCustomersSubscription?.unsubscribe();
    this.customerSearchSubscription?.unsubscribe();
    this.pendingLeadsSaveSubscription?.unsubscribe();
    this.relatedInstrumentsSubscription?.unsubscribe();
    this.investorCreationLookupSubscription?.unsubscribe();
    this.createInvestorSubscription?.unsubscribe();
    this.createInvestorGroupSubscription?.unsubscribe();
    this.notesSubscription?.unsubscribe();
    this.documentsSubscription?.unsubscribe();
    this.noteMutationSubscription?.unsubscribe();
    this.leadMutationSubscription?.unsubscribe();
    this.nonNamedDealersSubscription?.unsubscribe();
    this.availableNonNamedDealersSubscription?.unsubscribe();
    this.documentMutationSubscription?.unsubscribe();
    this.addDocumentModalCloseObserver?.disconnect();
    this.addInvestorsModalCloseObserver?.disconnect();
    this.tabsInstance?.dispose?.();
  }

  protected onRetry(): void {
    this.loadDetail();
  }

  protected onProceedWithNewBond(): void {
    if (!this.tradeId || this.isInitializing()) {
      return;
    }

    const detail = this.detail();
    if (!detail) {
      this.initializeError.set('Bond detail is unavailable. Please refresh and try again.');
      return;
    }

    const payload = this.toBondDetailUpsertRequest(detail);

    this.isInitializing.set(true);
    this.initializeError.set('');
    this.initializeSubscription?.unsubscribe();

    this.initializeSubscription = this.bondDetailService.saveBondDetail(payload).subscribe({
      next: () => {
        this.isInitializing.set(false);
        window.KTToast?.show({
          message: 'Bond detail was created in Frida.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: () => {
        this.isInitializing.set(false);
        this.initializeError.set('Unable to create this bond in Frida. Please try again.');
      },
    });
  }

  protected isFrnSecurity(): boolean {
    const securityType = this.detail()?.securityType.trim().toUpperCase() ?? '';
    return securityType === 'FRN';
  }

  protected isCdnSecurity(): boolean {
    const securityType = this.detail()?.securityType.trim().toUpperCase() ?? '';
    return securityType === 'CDN';
  }

  protected isCallBond(detail: BondDetailModel): boolean {
    const normalize = (value: string): string => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');
    return normalize(detail.marketType).startsWith('CALL')
      || normalize(detail.securityType).startsWith('CALL');
  }

  protected primaryRateMaturityValue(): string {
    const detail = this.detail();
    if (!detail) {
      return '-';
    }

    const maturityDate = this.formatSettlementDate(detail.maturityDate);
    if (this.isFrnSecurity()) {
      const indexWithSpread = this.formatIndexWithSpread(detail.indexName, detail.spread);
      return `${indexWithSpread} \u00A0\u00A0 ${maturityDate}`.trim();
    }

    const coupon = this.formatNullableNumber(detail.coupon, 4);
    return `${coupon} \u00A0\u00A0 ${maturityDate}`.trim();
  }

  protected hasAutoTranche(): boolean {
    return Boolean(this.detail()?.autoTranche.trim());
  }

  protected isAutoTrancheApplied(): boolean {
    const detail = this.detail();
    if (!detail) {
      return false;
    }

    const tranche = detail.tranche?.trim() ?? '';
    const autoTranche = detail.autoTranche?.trim() ?? '';
    if (!autoTranche) {
      return false;
    }

    return tranche === autoTranche;
  }

  protected onAutoTrancheToggle(isChecked: boolean): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      if (!isChecked) {
        return {
          ...current,
          tranche: '',
        };
      }

      if (!current.autoTranche.trim()) {
        return current;
      }

      return {
        ...current,
        tranche: current.autoTranche,
      };
    });

    this.hasUnsavedChanges.set(true);
  }

  protected hasAutoUs3MlCost(): boolean {
    return this.detail()?.autoUs3MlCost !== null;
  }

  protected isAutoUs3MlCostApplied(): boolean {
    return this.autoUs3MlCostChecked();
  }

  protected onAutoUs3MlCostToggle(isChecked: boolean): void {
    this.autoUs3MlCostChecked.set(isChecked);

    if (this.isRepurcMarket()) {
      this.detail.update((current) => {
        if (!current) {
          return current;
        }

        if (!isChecked) {
          const restoredValue = this.repurcOriginalCostBeforeAuto();
          this.repurcOriginalCostBeforeAuto.set(null);

          return {
            ...current,
            repurcOriginalCost: restoredValue,
          };
        }

        if (current.autoUs3MlCost === null) {
          this.autoUs3MlCostChecked.set(false);
          return current;
        }

        this.repurcOriginalCostBeforeAuto.set(current.repurcOriginalCost ?? null);

        return {
          ...current,
          repurcOriginalCost: current.autoUs3MlCost,
        };
      });

      this.hasUnsavedChanges.set(true);
      return;
    }

    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      if (!isChecked) {
        return {
          ...current,
          us3MlCost: null,
        };
      }

      if (current.autoUs3MlCost === null) {
        this.autoUs3MlCostChecked.set(false);
        return current;
      }

      return {
        ...current,
        us3MlCost: current.autoUs3MlCost,
      };
    });

    this.hasUnsavedChanges.set(true);
  }

  protected hasAutoSofrCost(): boolean {
    return this.detail()?.autoSofrCost !== null;
  }

  protected isAutoSofrCostApplied(): boolean {
    return this.autoSofrCostChecked();
  }

  protected onAutoSofrCostToggle(isChecked: boolean): void {
    this.autoSofrCostChecked.set(isChecked);

    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      if (!isChecked) {
        return {
          ...current,
          sofrCost: null,
        };
      }

      if (current.autoSofrCost === null) {
        this.autoSofrCostChecked.set(false);
        return current;
      }

      return {
        ...current,
        sofrCost: current.autoSofrCost,
      };
    });

    this.hasUnsavedChanges.set(true);
  }

  protected hasFeesInPricingSupplement(): boolean {
    return Boolean(this.detail()?.issFeeCurrency.trim());
  }

  protected saveButtonLabel(): string {
    return this.saveIntent() === 'save' ? 'Save Changes' : 'Save & Close';
  }

  protected shouldShowBondFeesSection(): boolean {
    return !this.isRepurcMarket() && !this.isCdnSecurity();
  }

  protected shouldShowInvestorAndLeadsSections(): boolean {
    const detail = this.detail();
    if (!detail) {
      return false;
    }

    return detail.isSale || this.isRepurcMarket();
  }

  protected shouldShowInvestorsSection(): boolean {
    return this.shouldShowInvestorAndLeadsSections() && !this.isRepurcMarket();
  }

  protected shouldShowLeadsCoLeadsSection(): boolean {
    return this.shouldShowInvestorAndLeadsSections() && !this.isCdnSecurity();
  }

  protected shouldShowNonNamedDealersSection(): boolean {
    return this.shouldShowInvestorAndLeadsSections() && this.isCdnSecurity();
  }

  protected shouldShowDocumentationSection(): boolean {
    return !this.isCdnSecurity();
  }

  protected leadsTabTitle(): string {
    if (this.isCdnSecurity()) {
      return 'Non Named Dealers';
    }

    return this.isRepurcMarket() ? 'Leads' : 'Leads / Co-Leads';
  }

  protected addLeadButtonLabel(): string {
    return this.isCdnSecurity() ? 'Add Dealer' : 'Add Leads';
  }

  protected leadModalTitle(): string {
    if (this.isCdnSecurity()) {
      return 'Add Non Named Dealer';
    }

    return this.editingLeadRowKey() === null ? 'Add Leads / Co-Leads' : 'Edit Lead / Co-Lead';
  }

  protected leadSubmitLabel(): string {
    if (this.isCdnSecurity()) {
      return this.addLeadButtonLabel();
    }

    return this.editingLeadRowKey() === null ? this.addLeadButtonLabel() : 'Save Changes';
  }

  protected leadRoleOptions(): string[] {
    const detail = this.detail();
    if (!detail) {
      return ['LEAD', 'CO-LEAD'];
    }

    if (this.isRepurcMarket() || this.isCdnSecurity()) {
      return ['LEAD'];
    }

    return ['LEAD', 'CO-LEAD'];
  }

  protected isLeadRoleLocked(): boolean {
    return this.isRepurcMarket() || this.isCdnSecurity();
  }

  protected isArbitrageNotionalDisabled(): boolean {
    const detail = this.detail();
    if (!detail) {
      return false;
    }

    if (this.isCdnSecurity()) {
      return true;
    }

    return this.normalizeUiLeadRole(this.addLeadForm().role) === 'CO-LEAD';
  }

  protected groupedCustomersCount(): number {
    return this.groupedCustomers().reduce((total, group) => total + group.customers.length, 0);
  }

  protected updateCustomerSearchTerm(value: string): void {
    this.customerSearchTerm.set(value);
    this.customerSearchInput.next(value);
  }

  protected searchGroupedCustomers(): void {
    this.loadGroupedCustomers(this.customerSearchTerm().trim());
  }

  protected isSelectableCustomerGroup(group: DwCustomerGroup): boolean {
    const parentId = group.parent.trim();
    return parentId !== '' && parentId !== DEFAULT_CUSTOMER_PARENT;
  }

  protected customerGroupLabel(group: DwCustomerGroup): string {
    if (!this.isSelectableCustomerGroup(group)) {
      return 'No parent';
    }

    return group.parentLegalName.trim() || group.parent;
  }

  protected isCustomerGroupExpanded(group: DwCustomerGroup): boolean {
    return !this.isSelectableCustomerGroup(group)
      || this.appliedCustomerSearchTerm().trim() !== ''
      || this.expandedCustomerGroups().has(group.parent);
  }

  protected toggleCustomerGroup(group: DwCustomerGroup): void {
    this.expandedCustomerGroups.update((current) => {
      const next = new Set(current);
      if (next.has(group.parent)) {
        next.delete(group.parent);
      } else {
        next.add(group.parent);
      }
      return next;
    });
  }

  protected isLeadCustomerAlreadyAdded(customerId: string): boolean {
    const normalizedId = customerId.trim().toUpperCase();
    return (this.detail()?.tradeLead ?? []).some((lead) => lead.customerId.trim().toUpperCase() === normalizedId)
      || this.isLeadCustomerPending(customerId);
  }

  protected isLeadCustomerPending(customerId: string): boolean {
    const normalizedId = customerId.trim().toUpperCase();
    return this.pendingLeads().some((row) => row.customerId.trim().toUpperCase() === normalizedId);
  }

  protected onPendingLeadRoleChange(customerId: string, value: string): void {
    const role = this.normalizeUiLeadRole(value);
    this.pendingLeads.update((rows) =>
      rows.map((row) => (row.customerId === customerId ? { ...row, role, error: '' } : row)),
    );
  }

  protected removePendingLead(customerId: string): void {
    this.pendingLeads.update((rows) => rows.filter((row) => row.customerId !== customerId));
  }

  protected discardPendingLeads(): void {
    if (this.isSavingPendingLeads()) {
      return;
    }

    this.pendingLeads.set([]);
  }

  protected savePendingLead(row: PendingLeadRow): void {
    this.savePendingLeadRows([row]);
  }

  protected saveAllPendingLeads(): void {
    this.savePendingLeadRows(this.pendingLeads().filter((row) => row.role.trim() !== ''));
  }

  private savePendingLeadRows(rows: PendingLeadRow[]): void {
    const tradeId = this.detail()?.tradeId;
    if (!tradeId || this.isSavingPendingLeads() || this.isMutatingLead()) {
      return;
    }

    const missingRole = rows.find((row) => row.role.trim() === '');
    if (missingRole || rows.length === 0) {
      window.KTToast?.show({ message: 'Select a Role before saving.', variant: 'warning' });
      return;
    }

    const ids = new Set(rows.map((row) => row.customerId));
    this.isSavingPendingLeads.set(true);
    this.pendingLeads.update((current) =>
      current.map((row) => (ids.has(row.customerId) ? { ...row, saving: true, error: '' } : row)),
    );

    let savedCount = 0;
    let failedCount = 0;
    this.pendingLeadsSaveSubscription?.unsubscribe();
    this.pendingLeadsSaveSubscription = from(rows).pipe(
      concatMap((row) =>
        this.bondDetailService.createBondTradeLead({
          tradeId,
          customerId: row.customerId,
          role: this.toApiLeadRole(row.role),
          arbitrageNotional: null,
          feeLeadColead: null,
        }).pipe(
          map((response) => ({ row, ok: true as const, response })),
          catchError((error: unknown) => of({ row, ok: false as const, error })),
        ),
      ),
    ).subscribe({
      next: (result) => {
        if (result.ok) {
          savedCount++;
          this.upsertTradeLeadInDetail({
            tradeId: result.response.tradeId || tradeId,
            customerId: result.row.customerId,
            customerName: result.response.customerName || result.row.customerName,
            role: this.normalizeUiLeadRole(result.response.role) || result.row.role,
            arbitrageNotional: result.response.arbitrageNotional,
            feeLeadColead: result.response.feeLeadColead,
          });
          this.removePendingLead(result.row.customerId);
          return;
        }

        failedCount++;
        const message = resolveApiErrorMessage('Unable to save this lead.', result.error);
        this.pendingLeads.update((current) =>
          current.map((row) =>
            row.customerId === result.row.customerId ? { ...row, saving: false, error: message } : row,
          ),
        );
      },
      complete: () => {
        this.isSavingPendingLeads.set(false);
        if (failedCount === 0) {
          window.KTToast?.show({
            message: savedCount === 1 ? '1 lead saved successfully.' : `${savedCount} leads saved successfully.`,
            variant: 'success',
          });
        } else {
          window.KTToast?.show({
            message: `${savedCount} saved, ${failedCount} failed. Review the highlighted rows.`,
            variant: savedCount > 0 ? 'warning' : 'destructive',
          });
        }
      },
    });
  }

  protected isLeadCustomerSelected(customerId: string): boolean {
    const normalizedId = customerId.trim().toUpperCase();
    return this.selectedLeadCustomers().some((item) => item.id.trim().toUpperCase() === normalizedId);
  }

  protected toggleLeadCustomerGroup(group: DwCustomerGroup): void {
    if (!this.isSelectableCustomerGroup(group)) {
      return;
    }

    this.toggleLeadCustomerSelection({
      id: group.parent.trim(),
      legalName: this.customerGroupLabel(group),
    });
  }

  protected toggleLeadCustomer(customer: DwCustomerItem): void {
    this.toggleLeadCustomerSelection({
      id: customer.id.trim(),
      legalName: customer.legalName.trim() || customer.id.trim(),
    });
  }

  protected removeSelectedLeadCustomer(customerId: string): void {
    this.selectedLeadCustomers.update((current) => current.filter((item) => item.id !== customerId));
  }

  protected clearSelectedLeadCustomers(): void {
    this.selectedLeadCustomers.set([]);
  }

  private toggleLeadCustomerSelection(selection: LeadCustomerSelection): void {
    if (!selection.id || this.isLeadCustomerAlreadyAdded(selection.id)) {
      return;
    }

    if (this.isLeadCustomerSelected(selection.id)) {
      const normalizedId = selection.id.toUpperCase();
      this.selectedLeadCustomers.update((current) =>
        current.filter((item) => item.id.toUpperCase() !== normalizedId),
      );
    } else {
      this.selectedLeadCustomers.update((current) => [...current, selection]);
    }

    this.addFormError.set('');
  }

  protected leadRoleLabel(role: string): string {
    return this.normalizeUiLeadRole(role) || '-';
  }

  protected isInlineEditingLead(row: TradeLeadItem): boolean {
    return this.inlineEditingLeadCustomerId() === row.customerId;
  }

  protected startInlineLeadEdit(row: TradeLeadItem): void {
    const role = this.isRepurcMarket() ? 'LEAD' : this.normalizeUiLeadRole(row.role);
    this.inlineEditingLeadCustomerId.set(row.customerId);
    this.inlineLeadDraft.set({
      role,
      arbitrageNotional: role === 'CO-LEAD' || row.arbitrageNotional === null ? '' : String(row.arbitrageNotional),
    });
  }

  protected cancelInlineLeadEdit(): void {
    this.inlineEditingLeadCustomerId.set(null);
    this.inlineLeadDraft.set({ role: '', arbitrageNotional: '' });
  }

  protected onInlineLeadRoleChange(value: string): void {
    const role = this.normalizeUiLeadRole(value);
    this.inlineLeadDraft.update((current) => ({
      role,
      arbitrageNotional: role === 'CO-LEAD' ? '' : current.arbitrageNotional,
    }));
  }

  protected saveInlineLeadEdit(row: TradeLeadItem): void {
    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || this.isMutatingLead()) {
      return;
    }

    const draft = this.inlineLeadDraft();
    const arbitrageNotional = draft.role === 'CO-LEAD' ? null : row.arbitrageNotional;
    const customerId = row.customerId.trim();
    this.isMutatingLead.set(true);
    this.leadMutationSubscription?.unsubscribe();

    this.leadMutationSubscription = this.bondDetailService.updateBondTradeLead(currentDetail.tradeId, customerId, {
      role: this.toApiLeadRole(draft.role),
      arbitrageNotional,
      feeLeadColead: row.feeLeadColead,
    }).subscribe({
      next: () => {
        this.updateTradeLeadInDetail(row.customerId, {
          ...row,
          role: draft.role,
          arbitrageNotional,
        });
        this.isMutatingLead.set(false);
        this.cancelInlineLeadEdit();
        window.KTToast?.show({ message: 'Lead row updated successfully.', variant: 'success' });
      },
      error: (error: unknown) => {
        this.isMutatingLead.set(false);
        window.KTToast?.show({
          message: resolveApiErrorMessage('Unable to update lead row. Please try again.', error),
          variant: 'destructive',
        });
      },
    });
  }

  protected onLeadRoleChange(value: string): void {
    const normalizedRole = this.normalizeUiLeadRole(value);
    this.addLeadForm.update((current) => {
      const nextForm: AddLeadFormValue = {
        ...current,
        role: normalizedRole,
      };

      if (normalizedRole === 'CO-LEAD') {
        nextForm.arbitrageNotional = '';
      }

      return nextForm;
    });
  }

  protected onLeadUseCustomFeeChange(isChecked: boolean): void {
    this.addLeadForm.update((current) => {
      if (!isChecked) {
        return {
          ...current,
          useCustomFee: false,
          feeLeadColead: '',
          feePercentage: '',
        };
      }

      return {
        ...current,
        useCustomFee: true,
      };
    });

    if (isChecked) {
      this.syncLeadPercentageFromFee();
    }
  }

  protected onLeadFeePercentageChange(value: string): void {
    this.addLeadForm.update((current) => ({
      ...current,
      feePercentage: value,
    }));

    this.calculateLeadFeeFromPercentage();
  }

  protected bmkModalTitle(): string {
    return this.editingBmkId() === null
      ? 'Add Spread over other Benchmark'
      : 'Edit Spread over other Benchmark';
  }

  protected bmkSubmitLabel(): string {
    return this.editingBmkId() === null ? 'Add Reoffer' : 'Save Changes';
  }

  protected documentModalTitle(): string {
    return this.editingDocumentId() === null ? 'Upload Documentation' : 'Edit Documentation';
  }

  protected documentSubmitLabel(): string {
    return this.editingDocumentId() === null ? 'Upload Documents' : 'Save Changes';
  }

  protected totalInvestorAllocation(): number {
    const investors = this.detail()?.investors ?? [];
    return investors.reduce((total, investor) => total + (investor.allocation ?? 0), 0);
  }

  protected investorAllocationDelta(): number | null {
    const notional = this.detail()?.notional;
    if (notional === null || notional === undefined) {
      return null;
    }

    return notional - this.totalInvestorAllocation();
  }

  protected investorAllocationStatusLabel(): string {
    const delta = this.investorAllocationDelta();
    if (delta === null) {
      return 'Notional unavailable';
    }

    if (delta === 0) {
      return 'Matched';
    }

    return delta > 0 ? 'Under Allocated' : 'Over Allocated';
  }

  protected investorAllocationStatusClass(): string {
    const delta = this.investorAllocationDelta();
    if (delta === null) {
      return 'text-secondary-foreground';
    }

    if (delta === 0) {
      return 'text-success';
    }

    return 'text-destructive';
  }

  protected investorSummaryValueClass(): string {
    const delta = this.investorAllocationDelta();
    if (delta === null) {
      return 'text-secondary-foreground';
    }

    return delta === 0 ? 'text-success' : 'text-destructive';
  }

  protected investorSummaryColor(): string {
    const delta = this.investorAllocationDelta();
    if (delta === null) {
      return '#6b7280';
    }

    return delta === 0 ? '#16a34a' : '#dc2626';
  }

  protected investorSummaryContainerClass(): string {
    const delta = this.investorAllocationDelta();
    if (delta === null) {
      return 'bond-investor-summary-neutral';
    }

    return delta === 0 ? 'bond-investor-summary-match' : 'bond-investor-summary-mismatch';
  }

  protected absoluteValue(value: number | null): number {
    return Math.abs(value ?? 0);
  }

  protected trackBondType(_: number, item: SimpleCatalogOption): string {
    return `${item.id}-${item.name}`;
  }

  protected isBondTypeSelected(currentBondTypeId: number | null, optionId: number): boolean {
    if (currentBondTypeId === null || currentBondTypeId === undefined) {
      return false;
    }

    return String(currentBondTypeId) === String(optionId);
  }

  protected trackBondTradeBmk(_: number, item: BondTradeBmkItem): string {
    return `${item.id}-${item.benchmark}-${item.tenor}-${item.value}`;
  }

  protected trackAvailableReofferBmk(_: number, item: ReofferBmk): string {
    return `${item.id}-${item.name}`;
  }

  protected isReofferBmkSelected(reofferId: number): boolean {
    return this.selectedReofferBmkId() === reofferId;
  }

  protected selectReofferBmk(reoffer: ReofferBmk): void {
    if (this.isMutatingBmk()) {
      return;
    }

    this.selectedReofferBmkId.set(reoffer.id);
    this.selectedReofferBmkName.set(reoffer.name);
    this.addFormError.set('');
  }

  protected updateReofferBmkSearchTerm(value: string): void {
    this.reofferBmkSearchTerm.set(value);
  }

  protected trackInvestor(_: number, item: BondInvestorItem): string {
    return `${item.id}-${item.name}`;
  }

  protected trackAvailableInvestor(_: number, item: Investor): string {
    return `${item.id}-${item.name}`;
  }

  protected prepareAddInvestorsModal(): void {
    this.addInvestorsError.set('');
    this.investorCreateLookupError.set('');
    this.createInvestorError.set('');
    this.createInvestorGroupError.set('');
    this.investorModalActiveTab.set('select-allocate');
    this.investorModalSearchTerm.set('');
    this.investorModalPageIndex.set(1);
    this.investorModalPageSize.set(10);
    this.editingInvestorAllocationId.set(null);
    this.editingInvestorAllocationValue.set('');
    this.loadAvailableInvestors();
    this.loadInvestorCreateLookups();
  }

  protected resetAddInvestorsModal(): void {
    this.addInvestorsError.set('');
    this.investorCreateLookupError.set('');
    this.createInvestorError.set('');
    this.createInvestorGroupError.set('');
    this.investorModalActiveTab.set('select-allocate');
    this.investorModalSearchTerm.set('');
    this.investorModalPageIndex.set(1);
    this.investorModalPageSize.set(10);
    this.editingInvestorAllocationId.set(null);
    this.editingInvestorAllocationValue.set('');
    this.resetCreateInvestorForm();
    this.resetCreateInvestorGroupForm();
  }

  protected activateInvestorsModalTab(tab: InvestorsModalTab): void {
    this.investorModalActiveTab.set(tab);
    this.addInvestorsError.set('');
    this.createInvestorError.set('');
    this.createInvestorGroupError.set('');
    this.initializeInvestorModalSelects();
  }

  protected updateCreateInvestorField(
    field: keyof CreateInvestorInlineFormValue,
    value: string,
  ): void {
    this.createInvestorError.set('');
    this.createInvestorForm.update((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === 'countryId') {
        next.regionId = this.resolveRegionIdForCountry(value);
      }

      return next;
    });
  }

  protected updateCreateInvestorGroupField(
    field: keyof CreateInvestorGroupInlineFormValue,
    value: string,
  ): void {
    this.createInvestorGroupError.set('');
    this.createInvestorGroupForm.update((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === 'hqCountryId') {
        next.regionId = this.resolveRegionIdForCountry(value);
      }

      return next;
    });
  }

  protected createInvestorFromModal(): void {
    if (this.isCreatingInvestor() || this.isCreatingInvestorGroup()) {
      return;
    }

    const rawValue = this.createInvestorForm();
    const payloadDraft = this.toCreateInvestorPayloadDraft(rawValue);
    if (!payloadDraft) {
      this.createInvestorError.set('Name, Investor Type, Country, Region, and Investor Group are required.');
      return;
    }

    const payload: CreateInvestorRequest = {
      ...payloadDraft,
      createdBy: '',
    };

    this.isCreatingInvestor.set(true);
    this.createInvestorError.set('');
    this.createInvestorSubscription?.unsubscribe();

    this.createInvestorSubscription = this.investorService.createInvestor(payload).subscribe({
      next: () => {
        const createdName = payload.name;
        this.isCreatingInvestor.set(false);
        this.resetCreateInvestorForm();
        this.investorModalSearchTerm.set(createdName);
        this.investorModalPageIndex.set(1);
        this.activateInvestorsModalTab('select-allocate');
        this.loadAvailableInvestors();
        window.KTToast?.show({
          message: 'Investor created successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isCreatingInvestor.set(false);
        this.createInvestorError.set(resolveApiErrorMessage('Unable to create investor. Please try again.', error));
      },
    });
  }

  protected createInvestorGroupFromModal(): void {
    if (this.isCreatingInvestorGroup() || this.isCreatingInvestor()) {
      return;
    }

    const rawValue = this.createInvestorGroupForm();
    const payload: CreateInvestorGroupRequest = {
      ...this.toCreateInvestorGroupPayloadDraft(rawValue),
      createdBy: '',
    };

    if (!payload.name) {
      this.createInvestorGroupError.set('Name is required.');
      return;
    }

    this.isCreatingInvestorGroup.set(true);
    this.createInvestorGroupError.set('');
    this.createInvestorGroupSubscription?.unsubscribe();

    this.createInvestorGroupSubscription = this.investorGroupService.createInvestorGroup(payload).subscribe({
      next: () => {
        const createdGroupName = payload.name;
        this.isCreatingInvestorGroup.set(false);
        this.resetCreateInvestorGroupForm();
        this.refreshInvestorGroupsLookup(createdGroupName);
        this.activateInvestorsModalTab('create-investor');
        window.KTToast?.show({
          message: 'Investor group created successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isCreatingInvestorGroup.set(false);
        this.createInvestorGroupError.set(resolveApiErrorMessage('Unable to create investor group. Please try again.', error));
      },
    });
  }

  protected updateInvestorModalSearchTerm(value: string): void {
    this.investorModalSearchTerm.set(value);
    this.investorModalPageIndex.set(1);
  }

  protected updateInvestorModalPageSize(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }

    this.investorModalPageSize.set(parsed);
    this.investorModalPageIndex.set(1);
  }

  protected goToPreviousInvestorModalPage(): void {
    if (this.investorModalCurrentPage() <= 1) {
      return;
    }

    this.investorModalPageIndex.set(this.investorModalCurrentPage() - 1);
  }

  protected goToNextInvestorModalPage(): void {
    if (this.investorModalCurrentPage() >= this.investorModalTotalPages()) {
      return;
    }

    this.investorModalPageIndex.set(this.investorModalCurrentPage() + 1);
  }

  protected isInvestorAllocationEditing(investorId: number): boolean {
    return this.editingInvestorAllocationId() === investorId;
  }

  protected startMainInvestorAllocationEdit(investor: BondInvestorItem): void {
    if (this.isMutatingInvestor()) {
      return;
    }

    this.addInvestorsError.set('');
    this.editingMainInvestorAllocationId.set(investor.id);
    this.editingMainInvestorAllocationValue.set(investor.allocation === null ? '' : String(investor.allocation));
  }

  protected isMainInvestorAllocationEditing(investorId: number): boolean {
    return this.editingMainInvestorAllocationId() === investorId;
  }

  protected updateMainInvestorAllocationValue(value: string): void {
    this.editingMainInvestorAllocationValue.set(value);
  }

  protected cancelMainInvestorAllocationEdit(investorId: number): void {
    if (!this.isMainInvestorAllocationEditing(investorId)) {
      return;
    }

    this.addInvestorsError.set('');
    this.editingMainInvestorAllocationId.set(null);
    this.editingMainInvestorAllocationValue.set('');
  }

  protected onMainInvestorAllocationInputKeydown(event: KeyboardEvent): void {
    this.handleAllocationShortcutKeydown(event, (nextValue) => {
      this.editingMainInvestorAllocationValue.set(nextValue);
    });
  }

  protected saveMainInvestorAllocation(investor: BondInvestorItem): void {
    if (this.isMutatingInvestor() || !this.isMainInvestorAllocationEditing(investor.id)) {
      return;
    }

    const allocation = this.parseInvestorAllocation(this.editingMainInvestorAllocationValue());
    if (allocation === null) {
      this.addInvestorsError.set('Allocation is required and must be a valid number.');
      return;
    }

    const currentDetail = this.detail();
    if (!currentDetail?.tradeId) {
      return;
    }

    this.isMutatingInvestor.set(true);
    this.addInvestorsError.set('');

    this.bondDetailService.updateBondInvestor(currentDetail.tradeId, investor.id, {
      investorAllocation: allocation,
    }).subscribe({
      next: () => {
        this.updateInvestorAllocationInDetail(investor.id, allocation);
        this.editingMainInvestorAllocationId.set(null);
        this.editingMainInvestorAllocationValue.set('');
        this.isMutatingInvestor.set(false);
        window.KTToast?.show({
          message: 'Investor allocation updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingInvestor.set(false);
        this.addInvestorsError.set(resolveApiErrorMessage('Unable to update investor allocation. Please try again.', error));
      },
    });
  }

  protected requestDeleteMainInvestor(investor: BondInvestorItem): void {
    if (this.isMutatingInvestor()) {
      return;
    }

    this.pendingInvestorDeleteId.set(investor.id);
    this.pendingInvestorDeleteName.set(investor.name || String(investor.id));
    this.modalController.open(this.deleteInvestorModalId);
  }

  protected cancelDeleteMainInvestor(): void {
    if (this.isMutatingInvestor()) {
      return;
    }

    this.pendingInvestorDeleteId.set(null);
    this.pendingInvestorDeleteName.set('');
  }

  protected confirmDeleteMainInvestor(): void {
    const investorId = this.pendingInvestorDeleteId();
    const currentDetail = this.detail();
    if (this.isMutatingInvestor() || investorId === null || !currentDetail?.tradeId) {
      return;
    }

    this.isMutatingInvestor.set(true);
    this.addInvestorsError.set('');

    this.bondDetailService.deleteBondInvestor(currentDetail.tradeId, investorId).subscribe({
      next: () => {
        this.removeInvestorFromDetail(investorId);
        this.modalController.close(this.deleteInvestorModalId);
        this.pendingInvestorDeleteId.set(null);
        this.pendingInvestorDeleteName.set('');
        this.editingMainInvestorAllocationId.set(null);
        this.editingMainInvestorAllocationValue.set('');
        this.isMutatingInvestor.set(false);
        window.KTToast?.show({
          message: 'Investor removed from bond successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingInvestor.set(false);
        this.addInvestorsError.set(resolveApiErrorMessage('Unable to delete investor from this bond. Please try again.', error));
      },
    });
  }

  protected startInvestorAllocationEdit(investor: InvestorModalRow): void {
    if (this.isMutatingInvestor()) {
      return;
    }

    this.addInvestorsError.set('');
    this.editingInvestorAllocationId.set(investor.id);
    this.editingInvestorAllocationValue.set(investor.currentAllocation === null ? '' : String(investor.currentAllocation));
  }

  protected cancelInvestorAllocationEdit(investorId: number): void {
    if (!this.isInvestorAllocationEditing(investorId)) {
      return;
    }

    this.addInvestorsError.set('');
    this.editingInvestorAllocationId.set(null);
    this.editingInvestorAllocationValue.set('');
  }

  protected deleteInvestorFromModal(investor: InvestorModalRow): void {
    if (this.isMutatingInvestor() || !investor.isAdded) {
      return;
    }

    const currentDetail = this.detail();
    if (!currentDetail?.tradeId) {
      return;
    }

    this.isMutatingInvestor.set(true);
    this.addInvestorsError.set('');

    this.bondDetailService.deleteBondInvestor(currentDetail.tradeId, investor.id).subscribe({
      next: () => {
        this.removeInvestorFromDetail(investor.id);
        this.isMutatingInvestor.set(false);
        this.editingInvestorAllocationId.set(null);
        this.editingInvestorAllocationValue.set('');
        window.KTToast?.show({
          message: 'Investor removed from bond successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingInvestor.set(false);
        this.addInvestorsError.set(resolveApiErrorMessage('Unable to delete investor from this bond. Please try again.', error));
      },
    });
  }

  protected updateInvestorAllocationValue(value: string): void {
    this.editingInvestorAllocationValue.set(value);
  }

  protected onInvestorAllocationInputKeydown(event: KeyboardEvent): void {
    this.handleAllocationShortcutKeydown(event, (nextValue) => {
      this.editingInvestorAllocationValue.set(nextValue);
    });
  }

  protected saveInvestorAllocationRow(investor: InvestorModalRow): void {
    if (!this.isInvestorAllocationEditing(investor.id) || this.isMutatingInvestor()) {
      return;
    }

    const allocation = this.parseInvestorAllocation(this.editingInvestorAllocationValue());
    if (allocation === null) {
      this.addInvestorsError.set('Allocation is required and must be a valid number.');
      return;
    }

    const currentDetail = this.detail();
    if (!currentDetail?.tradeId) {
      return;
    }

    this.isMutatingInvestor.set(true);
    this.addInvestorsError.set('');

    if (investor.isAdded) {
      this.bondDetailService.updateBondInvestor(currentDetail.tradeId, investor.id, {
        investorAllocation: allocation,
      }).subscribe({
        next: () => {
          this.updateInvestorAllocationInDetail(investor.id, allocation);
          this.isMutatingInvestor.set(false);
          this.editingInvestorAllocationId.set(null);
          this.editingInvestorAllocationValue.set('');
          window.KTToast?.show({
            message: 'Investor allocation updated successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingInvestor.set(false);
          this.addInvestorsError.set(resolveApiErrorMessage('Unable to update investor allocation. Please try again.', error));
        },
      });

      return;
    }

    this.bondDetailService.createBondInvestor({
      tradeId: currentDetail.tradeId,
      investorId: investor.id,
      investorAllocation: allocation,
    }).subscribe({
      next: () => {
        this.addInvestorToDetail(investor, allocation);
        this.isMutatingInvestor.set(false);
        this.editingInvestorAllocationId.set(null);
        this.editingInvestorAllocationValue.set('');
        window.KTToast?.show({
          message: 'Investor added successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingInvestor.set(false);
        this.addInvestorsError.set(resolveApiErrorMessage('Unable to add investor to this bond. Please try again.', error));
      },
    });
  }

  protected closeAddInvestorsModal(): void {
    this.modalController.close(this.addInvestorsModalId);
    this.resetAddInvestorsModal();
  }

  protected formatCurrencyWithCode(value: number | null, currencyCode: string): string {
    const normalizedCurrency = (currencyCode || '-').trim() || '-';
    if (value === null) {
      return `${normalizedCurrency} -`;
    }

    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

    return `${normalizedCurrency} ${formattedValue}`;
  }

  protected hasPreviousInvestorModalPage(): boolean {
    return this.investorModalCurrentPage() > 1;
  }

  protected hasNextInvestorModalPage(): boolean {
    return this.investorModalCurrentPage() < this.investorModalTotalPages();
  }

  private parseInvestorAllocation(rawValue: string): number | null {
    const normalized = rawValue.replaceAll(',', '').trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  }

  private resetCreateInvestorForm(): void {
    this.createInvestorForm.set({
      name: '',
      investorTypeId: '',
      countryId: '',
      regionId: '',
      investorGroupId: '',
    });
  }

  private resetCreateInvestorGroupForm(): void {
    this.createInvestorGroupForm.set({
      name: '',
      hqCountryId: '',
      regionId: '',
    });
  }

  private toCreateInvestorPayloadDraft(rawValue: CreateInvestorInlineFormValue): Omit<CreateInvestorRequest, 'createdBy'> | null {
    const investorTypeId = this.parsePositiveId(rawValue.investorTypeId);
    const countryId = this.parsePositiveId(rawValue.countryId);
    const regionId = this.parsePositiveId(rawValue.regionId);
    const investorGroupId = this.parsePositiveId(rawValue.investorGroupId);
    const name = rawValue.name.trim();

    if (!name || investorTypeId === null || countryId === null || regionId === null || investorGroupId === null) {
      return null;
    }

    return {
      name,
      investorTypeId,
      countryId,
      regionId,
      investorGroupId,
    };
  }

  private toCreateInvestorGroupPayloadDraft(rawValue: CreateInvestorGroupInlineFormValue): Omit<CreateInvestorGroupRequest, 'createdBy'> {
    return {
      name: rawValue.name.trim(),
      hqCountryId: this.parseNullableId(rawValue.hqCountryId),
      regionId: this.parseNullableId(rawValue.regionId),
    };
  }

  private parsePositiveId(rawId: string): number | null {
    const parsed = Number(rawId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private parseNullableId(rawId: string): number | null {
    const parsed = Number(rawId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private resolveRegionIdForCountry(countryIdValue: string): string {
    const countryId = Number(countryIdValue);
    if (!Number.isFinite(countryId) || countryId <= 0) {
      return '';
    }

    const matched = this.investorModalCountries().find((entry) => entry.id === countryId);
    if (!matched || matched.regionId === null) {
      return '';
    }

    return String(matched.regionId);
  }

  private initializeInvestorModalSelects(): void {
    requestAnimationFrame(() => {
      window.KTSelect?.createInstances();
    });
  }

  private addInvestorToDetail(investor: InvestorModalRow, allocation: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const exists = current.investors.some((entry) => entry.id === investor.id);
      if (exists) {
        return {
          ...current,
          investors: current.investors.map((entry) => (entry.id === investor.id ? { ...entry, allocation } : entry)),
        };
      }

      const nextInvestor: BondInvestorItem = {
        id: investor.id,
        name: investor.name,
        investorTypeName: investor.investorTypeName,
        country: investor.country,
        region: investor.region,
        investorGroupName: investor.investorGroupName,
        allocation,
      };

      return {
        ...current,
        investors: [...current.investors, nextInvestor],
      };
    });
  }

  private updateInvestorAllocationInDetail(investorId: number, allocation: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        investors: current.investors.map((entry) => (entry.id === investorId ? { ...entry, allocation } : entry)),
      };
    });
  }

  private removeInvestorFromDetail(investorId: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        investors: current.investors.filter((entry) => entry.id !== investorId),
      };
    });
  }

  private handleAllocationShortcutKeydown(
    event: KeyboardEvent,
    setValue: (nextValue: string) => void,
  ): void {
    if (event.key !== 'm' && event.key !== 'M') {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    event.preventDefault();

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const nextValue = `${input.value.slice(0, start)}000000${input.value.slice(end)}`;

    setValue(nextValue);

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + 6, start + 6);
    });
  }

  protected trackTradeLead(_: number, item: TradeLeadItem): string {
    return `${item.tradeId}-${item.customerId}-${item.role}`;
  }

  protected trackRelatedInstrument(index: number, item: RelatedInstrumentItem): string {
    return `${item.instrumentType}-${item.tradeId}-${item.securityId}-${index}`;
  }

  protected relatedTradeDetailRoute(instrumentType: string): string {
    return instrumentType.trim().toUpperCase() === 'SWAP' ? '/swap-detail' : '/bond-detail';
  }

  protected startEditLeadRow(row: TradeLeadItem): void {
    this.cancelInlineLeadEdit();
    this.addFormError.set('');
    this.editingLeadRowKey.set(this.leadRowKey(row));
    this.editingLeadCustomerId.set(row.customerId);
    this.selectedLeadCustomers.set([]);

    const normalizedRole = this.isRepurcMarket() ? 'LEAD' : this.normalizeUiLeadRole(row.role);
    const feeLeadColead = row.feeLeadColead === null ? '' : String(row.feeLeadColead);
    const feePercentage = this.calculateLeadPercentageFromFee(row.feeLeadColead);

    this.addLeadForm.set({
      customerId: row.customerId,
      customerName: row.customerName,
      role: normalizedRole,
      arbitrageNotional: normalizedRole === 'CO-LEAD' ? '' : (row.arbitrageNotional === null ? '' : String(row.arbitrageNotional)),
      feeLeadColead,
      useCustomFee: row.feeLeadColead !== null,
      feePercentage,
    });

    this.customerSearchTerm.set('');
  }

  protected trackNonNamedDealer(_: number, item: NonNamedDealerItem): string {
    return `${item.id}-${item.name}`;
  }

  protected trackDocument(_: number, item: BondDocumentItem): string {
    return `${item.id}-${item.filename}-${item.description}`;
  }

  protected documentFilenames(documents: readonly BondDocumentItem[]): string[] {
    return documents.map((document) => document.filename);
  }

  protected renameDocument(document: BondDocumentItem, filename: string): void {
    this.saveDocumentInline(document, 'filename', filename);
  }

  protected updateDocumentDescription(document: BondDocumentItem, description: string | null): void {
    this.saveDocumentInline(document, 'description', description);
  }

  private saveDocumentInline(document: BondDocumentItem, field: 'filename' | 'description', value: string | null): void {
    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || this.isMutatingDocument()) {
      return;
    }

    const payload: DocumentUpdateRequest = {
      parentElementId: currentDetail.tradeId || null,
      securityId: currentDetail.secId || null,
      filename: field === 'filename' ? (value ?? document.filename) : document.filename,
      description: field === 'description' ? value : document.description || null,
      type: document.type || null,
      ezShareId: document.ezShareId || null,
      ezShareLink: document.ezShareLink || null,
      source: this.documentSource,
    };

    this.isMutatingDocument.set(true);
    const savingDocumentId = field === 'filename' ? this.renamingDocumentId : this.describingDocumentId;
    savingDocumentId.set(document.id);
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.bondDetailService.updateDocument(document.id, payload).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        this.loadDocumentsAttached(currentDetail.tradeId);
        window.KTToast?.show({
          message: field === 'filename' ? `Document renamed to '${value}'.` : 'Document description updated.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        window.KTToast?.show({
          message: resolveApiErrorMessage(
            field === 'filename'
              ? 'Unable to rename document. Please try again.'
              : 'Unable to update document description. Please try again.',
            error,
          ),
          variant: 'destructive',
        });
      },
    });
  }

  protected startEditDocumentRow(row: BondDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.addFormError.set('');
    this.editingDocumentId.set(row.id);
    this.addDocumentForm.set({
      description: row.description,
      filename: row.filename,
      files: [],
    });
  }

  protected requestDeleteDocumentRow(row: BondDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(row.id);
    this.pendingDocumentDeletePreview.set((row.filename || row.description || String(row.id)).trim().slice(0, 120));
  }

  protected cancelDeleteDocumentRow(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(null);
    this.pendingDocumentDeletePreview.set('');
  }

  protected trackNote(_: number, item: BondNoteItem): string {
    return `${item.id}-${item.createdAt}`;
  }

  protected noteModalTitle(): string {
    return this.editingNoteId() === null ? 'Add Note' : 'Edit Note';
  }

  protected noteSubmitLabel(): string {
    return this.editingNoteId() === null ? 'Add Note' : 'Save Changes';
  }

  protected startEditNoteRow(row: BondNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.addFormError.set('');
    this.editingNoteId.set(row.id);
    this.addNoteForm.set({ note: row.note });
  }

  protected requestDeleteNoteRow(row: BondNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.pendingNoteDeleteId.set(row.id);
    this.pendingNoteDeletePreview.set(row.note.trim().slice(0, 120));
    this.modalController.open(this.deleteNoteModalId);
  }

  protected cancelDeleteNoteRow(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.pendingNoteDeleteId.set(null);
    this.pendingNoteDeletePreview.set('');
  }

  protected confirmDeleteNoteRow(): void {
    const noteId = this.pendingNoteDeleteId();
    if (noteId === null || this.isMutatingNote()) {
      return;
    }

    this.isMutatingNote.set(true);
    this.addFormError.set('');
    this.noteMutationSubscription?.unsubscribe();

    this.noteMutationSubscription = this.bondDetailService.deleteNote(noteId).subscribe({
      next: () => {
        this.removeNoteFromDetail(noteId);
        this.loadNotes();
        this.modalController.close(this.deleteNoteModalId);
        this.pendingNoteDeleteId.set(null);
        this.pendingNoteDeletePreview.set('');
        this.isMutatingNote.set(false);
        window.KTToast?.show({
          message: 'Note deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingNote.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to delete note. Please try again.', error));
      },
    });
  }

  protected updateTextField(
    field:
      | 'bondPricingSupplement'
      | 'tranche'
      | 'bondGlRepurchase'
      | 'bondGlRefinance',
    value: string,
  ): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        [field]: value,
      };
    });

    this.hasUnsavedChanges.set(true);
  }

  protected updateNumberField(
    field:
      | 'us3MlCost'
      | 'sofrCost'
      | 'bondTheoreticalFee'
      | 'repurcOriginalCost'
      | 'repurcNewFundingCost',
    value: string,
  ): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const parsed = value.trim() === '' ? null : Number(value);
      return {
        ...current,
        [field]: Number.isFinite(parsed) ? parsed : null,
      };
    });

    this.hasUnsavedChanges.set(true);

    if (field === 'us3MlCost') {
      this.autoUs3MlCostChecked.set(false);
    }

    if (field === 'repurcOriginalCost') {
      this.autoUs3MlCostChecked.set(false);
      this.repurcOriginalCostBeforeAuto.set(null);
    }

    if (field === 'sofrCost') {
      this.autoSofrCostChecked.set(false);
    }
  }

  protected formatRepurcNumber(value: string | number | null): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '';
    }

    const parsed = Number(String(value).replaceAll(',', ''));
    if (!Number.isFinite(parsed)) {
      return '';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 20,
      useGrouping: true,
    }).format(parsed);
  }

  protected onRepurcNumberInput(event: Event, field: RepurcNumericField): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const caretPosition = input.selectionStart ?? rawValue.length;
    const rawBeforeCaret = rawValue.slice(0, caretPosition);
    const caretIsInFraction = rawBeforeCaret.includes('.');
    const digitsBeforeCaret = rawBeforeCaret.replace(/\D/g, '').length;
    const fractionDigitsBeforeCaret = caretIsInFraction
      ? rawBeforeCaret.slice(rawBeforeCaret.indexOf('.') + 1).replace(/\D/g, '').length
      : 0;
    const normalized = this.normalizeRepurcNumber(rawValue);

    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      if (field === 'bondGlRepurchase' || field === 'bondGlRefinance') {
        return {
          ...current,
          [field]: normalized,
        };
      }

      const parsed = normalized === '' || normalized === '-' ? null : Number(normalized);
      return {
        ...current,
        [field]: parsed !== null && Number.isFinite(parsed) ? parsed : null,
      };
    });

    this.hasUnsavedChanges.set(true);
    if (field === 'us3MlCost') {
      this.autoUs3MlCostChecked.set(false);
    } else if (field === 'repurcOriginalCost') {
      this.autoUs3MlCostChecked.set(false);
      this.repurcOriginalCostBeforeAuto.set(null);
    }

    const formatted = this.formatRepurcNumber(normalized);
    input.value = formatted;
    this.restoreRepurcInputCaret(input, digitsBeforeCaret, fractionDigitsBeforeCaret, caretIsInFraction);
  }

  protected updateBondType(value: string): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const parsed = value.trim() === '' ? null : Number(value);

      return {
        ...current,
        bondTypeId: Number.isFinite(parsed) ? parsed : null,
      };
    });

    this.hasUnsavedChanges.set(true);
  }

  protected onBpsPerYearChange(value: string): void {
    const normalizedValue = value.trim();
    this.bpsPerYear.set(normalizedValue);

    const parsed = Number(normalizedValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return;
    }

    const autoBondTheoreticalFee = this.detail()?.autoBondTheoreticalFee;
    if (autoBondTheoreticalFee === null || autoBondTheoreticalFee === undefined) {
      return;
    }

    const calculatedValue = Math.round(parsed * autoBondTheoreticalFee);
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        bondTheoreticalFee: calculatedValue,
      };
    });
    this.hasUnsavedChanges.set(true);
  }

  protected onDocumentationFlagChange(
    flag: 'hasLegalDocument' | 'hasTermSheet' | 'hasRta' | 'executedDocsSignatureRemoved' | 'isPrivatePricingSupplement',
    checked: boolean,
  ): void {
    const current = this.detail();
    if (!current || current[flag] === checked) {
      return;
    }

    this.detail.set({ ...current, [flag]: checked });
    this.hasUnsavedChanges.set(true);
  }

  protected prepareSaveIntent(intent: SaveIntent): void {
    this.saveIntent.set(intent);
    this.saveInfo.set('');
    this.saveError.set('');
  }

  protected confirmSave(): void {
    const detail = this.detail();
    if (!detail || this.isSaving()) {
      return;
    }

    const action = this.saveIntent();
    const payload = this.toBondDetailUpsertRequest(detail);

    this.isSaving.set(true);
    this.saveError.set('');
    this.saveSubscription?.unsubscribe();

    this.saveSubscription = this.bondDetailService.saveBondDetail(payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.hasUnsavedChanges.set(false);
        this.pricingSupplementReloadKey.update((value) => value + 1);
        this.saveInfo.set('');
        this.modalController.close(this.saveModalId);

        window.KTToast?.show({
          message: action === 'save'
            ? 'Bond detail saved successfully.'
            : 'Bond detail saved successfully. Closing view...',
          variant: 'success',
        });

        if (action === 'save-and-close') {
          void this.router.navigate(['/bonds']);
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.saveError.set('Unable to save bond detail. Please review your changes and try again.');
      },
    });
  }

  protected onSaveModalCancel(): void {
    if (this.isSaving()) {
      return;
    }

    this.saveError.set('');
  }

  protected onCloseRequested(event?: Event): void {
    event?.preventDefault();

    if (!this.hasUnsavedChanges()) {
      void this.router.navigate(['/bonds']);
      return;
    }

    this.modalController.open(this.discardChangesModalId);
  }

  protected discardChangesAndClose(): void {
    this.hasUnsavedChanges.set(false);
    this.modalController.close(this.discardChangesModalId);
    void this.router.navigate(['/bonds']);
  }

  protected confirmDeleteBond(): void {
    const tradeId = (this.detail()?.tradeId || this.tradeId).trim();
    if (!tradeId || this.isDeletingBond()) {
      return;
    }

    this.isDeletingBond.set(true);
    this.bondDetailService.deleteBond(tradeId).subscribe({
      next: () => {
        this.isDeletingBond.set(false);
        this.modalController.close(this.deleteModalId);
        window.KTToast?.show({
          message: 'Bond deleted successfully.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: (error: unknown) => {
        this.isDeletingBond.set(false);
        window.KTToast?.show({
          message: resolveApiErrorMessage('Unable to delete bond. Please try again.', error),
          variant: 'destructive',
        });
      },
    });
  }

  protected updateAddBmkField(field: keyof AddBmkFormValue, value: string): void {
    this.addBmkForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected prepareAddBmkModal(): void {
    this.resetAddBmkForm();
    this.loadAvailableReofferBmks();
  }

  protected updateAddLeadField(field: keyof AddLeadFormValue, value: string): void {
    this.addLeadForm.update((current) => ({
      ...current,
      [field]: value,
    }));

    if (field === 'feeLeadColead') {
      this.syncLeadPercentageFromFee();
    }
  }

  protected updateAddDocumentField(field: 'description' | 'filename', value: string): void {
    this.addDocumentForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    this.addDocumentForm.update((current) => ({
      ...current,
      files,
    }));
    this.documentUploadEntries.set(createDocumentUploadEntries(files));
    this.addFormError.set('');
  }

  protected updateAddNoteField(field: keyof AddNoteFormValue, value: string): void {
    this.addNoteForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected resetAddBmkForm(): void {
    this.addFormError.set('');
    this.editingBmkId.set(null);
    this.reofferBmkSearchTerm.set('');
    this.selectedReofferBmkId.set(null);
    this.selectedReofferBmkName.set('');
    this.addBmkForm.set({ value: '' });
  }

  protected startEditBmkRow(row: BondTradeBmkItem): void {
    this.addFormError.set('');
    this.editingBmkId.set(row.id);
    this.reofferBmkSearchTerm.set('');
    this.selectedReofferBmkId.set(row.id);
    this.selectedReofferBmkName.set(row.benchmark);
    this.addBmkForm.set({
      value: row.value,
    });
  }

  protected resetAddLeadForm(): void {
    this.addFormError.set('');
    this.editingLeadRowKey.set(null);
    this.editingLeadCustomerId.set(null);
    this.customerSearchTerm.set('');
    this.selectedLeadCustomers.set([]);
    this.expandedCustomerGroups.set(new Set<string>());

    const defaultRole = this.isRepurcMarket() || this.isCdnSecurity() ? 'LEAD' : '';

    this.addLeadForm.set({
      customerId: '',
      customerName: '',
      role: defaultRole,
      arbitrageNotional: '',
      feeLeadColead: '',
      useCustomFee: false,
      feePercentage: '',
    });
  }

  protected prepareAddLeadModal(): void {
    this.cancelInlineLeadEdit();
    this.resetAddLeadForm();
    this.loadGroupedCustomers();
  }

  protected resetAddDocumentForm(): void {
    this.addFormError.set('');
    this.editingDocumentId.set(null);
    this.documentUploadEntries.set([]);
    if (this.documentFilesInput) {
      this.documentFilesInput.nativeElement.value = '';
    }
    this.addDocumentForm.set({
      description: '',
      filename: '',
      files: [],
    });
  }

  private closeAddDocumentModalAfterMutation(): void {
    requestAnimationFrame(() => this.modalController.close(this.addDocumentModalId));
  }

  protected resetAddNoteForm(): void {
    this.addFormError.set('');
    this.editingNoteId.set(null);
    this.addNoteForm.set({ note: '' });
  }

  protected addBmkRow(): void {
    const form = this.addBmkForm();
    const detail = this.detail();
    if (!detail?.tradeId || this.isMutatingBmk()) {
      return;
    }

    const value = this.parseBmkValue(form.value);
    if (value === null) {
      this.addFormError.set('Spread over Benchmark in bps is required and must be a valid number.');
      return;
    }

    const editingBmkId = this.editingBmkId();
    this.isMutatingBmk.set(true);
    this.addFormError.set('');

    if (editingBmkId !== null) {
      this.bondDetailService.updateBondTradeBmk(detail.tradeId, editingBmkId, { value }).subscribe({
        next: () => {
          this.updateBondTradeBmkInDetail(editingBmkId, value);
          this.isMutatingBmk.set(false);
          this.resetAddBmkForm();
          this.modalController.close(this.addBmkModalId);
          window.KTToast?.show({
            message: 'Reoffer row updated successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingBmk.set(false);
          this.addFormError.set(resolveApiErrorMessage('Unable to update reoffer row. Please try again.', error));
        },
      });

      return;
    }

    const selectedReofferId = this.selectedReofferBmkId();
    const selectedReofferName = this.selectedReofferBmkName().trim();
    if (selectedReofferId === null || !selectedReofferName) {
      this.isMutatingBmk.set(false);
      this.addFormError.set('You must select one Reoffer BMK Name.');
      return;
    }

    this.bondDetailService.createBondTradeBmk({
      tradeId: detail.tradeId,
      reofferId: selectedReofferId,
      value,
    }).subscribe({
      next: () => {
        this.addBondTradeBmkToDetail(selectedReofferId, selectedReofferName, value);
        this.isMutatingBmk.set(false);
        this.resetAddBmkForm();
        this.modalController.close(this.addBmkModalId);
        window.KTToast?.show({
          message: 'Reoffer row created successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingBmk.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to create reoffer row. Please try again.', error));
      },
    });
  }

  protected deleteBmkRow(row: BondTradeBmkItem): void {
    this.requestDeleteBmkRow(row);
  }

  protected requestDeleteBmkRow(row: BondTradeBmkItem): void {
    if (this.isMutatingBmk()) {
      return;
    }

    this.pendingBmkDeleteId.set(row.id);
    this.pendingBmkDeleteName.set(row.benchmark || String(row.id));
    this.modalController.open(this.deleteBmkModalId);
  }

  protected cancelDeleteBmkRow(): void {
    if (this.isMutatingBmk()) {
      return;
    }

    this.pendingBmkDeleteId.set(null);
    this.pendingBmkDeleteName.set('');
  }

  protected confirmDeleteBmkRow(): void {
    const reofferId = this.pendingBmkDeleteId();
    const detail = this.detail();
    if (!detail?.tradeId || this.isMutatingBmk() || reofferId === null) {
      return;
    }

    this.isMutatingBmk.set(true);
    this.addFormError.set('');

    this.bondDetailService.deleteBondTradeBmk(detail.tradeId, reofferId).subscribe({
      next: () => {
        this.removeBondTradeBmkFromDetail(reofferId);
        this.modalController.close(this.deleteBmkModalId);
        this.pendingBmkDeleteId.set(null);
        this.pendingBmkDeleteName.set('');
        this.isMutatingBmk.set(false);
        window.KTToast?.show({
          message: 'Reoffer row deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingBmk.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to delete reoffer row. Please try again.', error));
      },
    });
  }

  protected addLeadOrDealerRow(): void {
    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || this.isMutatingLead()) {
      return;
    }

    const form = this.addLeadForm();
    const editingKey = this.editingLeadRowKey();
    if (editingKey === null) {
      this.addSelectedLeadsAsPending();
      return;
    }

    if (!form.customerId.trim() || !form.customerName.trim()) {
      this.addFormError.set('Please select a bank from the customer list.');
      return;
    }

    const normalizedRole = this.isRepurcMarket()
      ? 'LEAD'
      : this.normalizeUiLeadRole(form.role);
    const arbitrageNotional = this.isArbitrageNotionalDisabled()
      ? null
      : this.parseNullableNumber(form.arbitrageNotional);
    const feeLeadColead = form.useCustomFee
      ? this.parseNullableNumber(form.feeLeadColead)
      : null;

    this.isMutatingLead.set(true);
    this.addFormError.set('');
    this.leadMutationSubscription?.unsubscribe();

    const editCustomerId = this.editingLeadCustomerId() ?? form.customerId.trim();
    this.leadMutationSubscription = this.bondDetailService.updateBondTradeLead(currentDetail.tradeId, editCustomerId, {
      role: this.toApiLeadRole(normalizedRole),
      arbitrageNotional,
      feeLeadColead,
    }).subscribe({
      next: () => {
        this.updateTradeLeadInDetail(editCustomerId, {
          tradeId: currentDetail.tradeId,
          customerId: editCustomerId,
          customerName: form.customerName.trim(),
          role: normalizedRole,
          arbitrageNotional,
          feeLeadColead,
        });

        this.isMutatingLead.set(false);
        this.resetAddLeadForm();
        this.modalController.close(this.addLeadModalId);
        window.KTToast?.show({
          message: 'Lead row updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingLead.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to update lead row. Please try again.', error));
      },
    });
  }

  private addSelectedLeadsAsPending(): void {
    const selections = this.selectedLeadCustomers();
    if (selections.length === 0) {
      this.addFormError.set('Please select at least one bank.');
      return;
    }

    const defaultRole = this.isLeadRoleLocked() ? 'LEAD' : '';
    const newRows: PendingLeadRow[] = selections
      .filter((item) => !this.isLeadCustomerAlreadyAdded(item.id))
      .map((item) => ({
        customerId: item.id,
        customerName: item.legalName || item.id,
        role: defaultRole,
        saving: false,
        error: '',
      }));

    this.pendingLeads.update((current) => [...newRows, ...current]);
    this.addFormError.set('');
    this.resetAddLeadForm();
    this.modalController.close(this.addLeadModalId);
    window.KTToast?.show({
      message: newRows.length === 1
        ? '1 lead added to the table. Select a Role and save it.'
        : `${newRows.length} leads added to the table. Select a Role and save them.`,
      variant: 'info',
    });
  }

  protected deleteLeadRow(row: TradeLeadItem): void {
    if (this.isCdnSecurity() || this.isMutatingLead()) {
      return;
    }

    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || !row.customerId.trim()) {
      return;
    }

    this.isMutatingLead.set(true);
    this.addFormError.set('');
    this.leadMutationSubscription?.unsubscribe();

    this.leadMutationSubscription = this.bondDetailService.deleteBondTradeLead(
      currentDetail.tradeId,
      row.customerId.trim(),
    ).subscribe({
      next: () => {
        this.removeTradeLeadFromDetail(row.customerId.trim());
        if (this.inlineEditingLeadCustomerId() === row.customerId) {
          this.cancelInlineLeadEdit();
        }
        this.isMutatingLead.set(false);
        window.KTToast?.show({
          message: 'Lead row deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingLead.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to delete lead row. Please try again.', error));
      },
    });
  }

  protected addDocumentRow(): void {
    const form = this.addDocumentForm();
    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || this.isMutatingDocument()) {
      return;
    }

    const editingDocumentId = this.editingDocumentId();
    if (editingDocumentId !== null) {
      const filename = form.filename.trim();
      if (!filename) {
        this.addFormError.set('Filename is required.');
        return;
      }

      const currentDocument = currentDetail.documentsAttached.find((document) => document.id === editingDocumentId);
      if (!currentDocument) {
        this.addFormError.set('Unable to identify the document to update.');
        return;
      }

      const payload: DocumentUpdateRequest = {
        parentElementId: currentDetail.tradeId || null,
        securityId: currentDetail.secId || null,
        filename,
        description: form.description.trim() || null,
        type: currentDocument.type || null,
        ezShareId: currentDocument.ezShareId || null,
        ezShareLink: currentDocument.ezShareLink || null,
        source: this.documentSource,
      };

      this.isMutatingDocument.set(true);
      this.addFormError.set('');
      this.documentMutationSubscription?.unsubscribe();
      this.documentMutationSubscription = this.bondDetailService.updateDocument(editingDocumentId, payload).subscribe({
        next: () => {
          this.isMutatingDocument.set(false);
          this.closeAddDocumentModalAfterMutation();
          this.loadDocumentsAttached(currentDetail.tradeId);
          window.KTToast?.show({
            message: 'Document metadata updated successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingDocument.set(false);
          this.addFormError.set(resolveApiErrorMessage('Unable to update document. Please try again.', error));
        },
      });

      return;
    }

    if (form.files.length === 0) {
      this.addFormError.set('Select at least one file to upload.');
      return;
    }

    const invalidExtensions: string[] = [];
    const payloads: DocumentCreateRequest[] = [];
    const reservedFilenames = new Set(
      currentDetail.documentsAttached
        .map((document) => document.filename.trim().toUpperCase())
        .filter((filename) => filename.length > 0),
    );
    for (const file of form.files) {
      const extension = this.extractFileExtension(file.name);
      if (!extension || !this.isAllowedDocumentType(extension)) {
        invalidExtensions.push(file.name || '(unknown file)');
        continue;
      }

      let generatedFilename = '';
      let attempts = 0;
      do {
        generatedFilename = `${this.generateDocumentCode()}.${extension.slice(1)}`.toUpperCase();
        attempts += 1;
      } while (reservedFilenames.has(generatedFilename) && attempts < 10);

      if (reservedFilenames.has(generatedFilename)) {
        this.addFormError.set('Unable to generate a unique filename code. Please retry upload.');
        return;
      }
      reservedFilenames.add(generatedFilename);

      payloads.push({
        filename: generatedFilename,
        parentElementId: currentDetail.tradeId,
        securityId: currentDetail.secId || null,
        description: '',
        type: extension,
        source: this.documentSource,
        modifiedBy: this.resolveDocumentModifiedBy(),
        file,
      });
    }

    if (invalidExtensions.length > 0) {
      this.addFormError.set(
        `Unsupported file type: ${invalidExtensions.join(', ')}. Allowed types: ${this.allowedDocumentTypes.join(', ')}.`,
      );
      return;
    }

    if (payloads.length === 0) {
      this.addFormError.set('No valid files were selected for upload.');
      return;
    }

    this.isMutatingDocument.set(true);
    this.addFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = uploadDocumentsSequentially(
      payloads,
      (payload) => this.bondDetailService.createDocument(payload),
      (file, status, message) =>
        this.documentUploadEntries.update((entries) => updateDocumentUploadEntry(entries, file, status, message)),
    ).subscribe((outcomes) => {
      const summary = summarizeDocumentUploads(outcomes);
      this.isMutatingDocument.set(false);
      this.loadDocumentsAttached(currentDetail.tradeId);
      window.KTToast?.show(buildDocumentUploadToast(summary));

      if (summary.failed.length === 0) {
        this.closeAddDocumentModalAfterMutation();
        return;
      }

      this.addDocumentForm.update((current) => ({ ...current, files: summary.retryableFiles }));
      if (summary.retryableFiles.length > 0) {
        this.addFormError.set('Some files could not be uploaded. Upload again to retry only the failed files.');
      }
    });
  }

  protected confirmDeleteDocumentRow(): void {
    const documentId = this.pendingDocumentDeleteId();
    const currentDetail = this.detail();
    if (documentId === null || !currentDetail?.tradeId || this.isMutatingDocument()) {
      return;
    }

    this.isMutatingDocument.set(true);
    this.addFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.bondDetailService.deleteDocument(documentId).subscribe({
      next: () => {
        this.loadDocumentsAttached(currentDetail.tradeId);
        this.modalController.close(this.deleteDocumentModalId);
        this.pendingDocumentDeleteId.set(null);
        this.pendingDocumentDeletePreview.set('');
        this.isMutatingDocument.set(false);
        window.KTToast?.show({
          message: 'Document deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to delete document. Please try again.', error));
      },
    });
  }

  protected addNoteRow(): void {
    const form = this.addNoteForm();
    const noteText = form.note.trim();
    if (!noteText) {
      this.addFormError.set('Note text is required.');
      return;
    }

    if (noteText.length > 1600) {
      this.addFormError.set('Note text cannot exceed 1600 characters.');
      return;
    }

    const payload = this.buildNotePayload(noteText);
    if (!payload) {
      this.addFormError.set('Trade ID is required to save notes.');
      return;
    }

    const editingNoteId = this.editingNoteId();
    this.isMutatingNote.set(true);
    this.addFormError.set('');
    this.noteMutationSubscription?.unsubscribe();

    if (editingNoteId === null) {
      this.noteMutationSubscription = this.bondDetailService.createNote(payload).subscribe({
        next: (response) => {
          this.upsertNoteInDetail(this.toBondNoteItem(response));
          this.loadNotes();
          this.resetAddNoteForm();
          this.modalController.close(this.addNoteModalId);
          this.isMutatingNote.set(false);
          window.KTToast?.show({
            message: 'Note added successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingNote.set(false);
          this.addFormError.set(resolveApiErrorMessage('Unable to add note. Please try again.', error));
        },
      });

      return;
    }

    this.noteMutationSubscription = this.bondDetailService.updateNote(editingNoteId, payload).subscribe({
      next: () => {
        this.updateNoteInDetail(editingNoteId, payload.description, payload.modifiedBy);
        this.loadNotes();
        this.resetAddNoteForm();
        this.modalController.close(this.addNoteModalId);
        this.isMutatingNote.set(false);
        window.KTToast?.show({
          message: 'Note updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingNote.set(false);
        this.addFormError.set(resolveApiErrorMessage('Unable to update note. Please try again.', error));
      },
    });
  }

  protected formatNullableNumber(value: number | null, fractionDigits: number): string {
    if (value === null) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  protected formatCurrencyLike(value: number | null): string {
    if (value === null) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10,
    }).format(value);
  }

  protected formatAbsoluteCurrencyLike(value: number | null): string {
    if (value === null) {
      return '-';
    }

    return this.formatCurrencyLike(Math.abs(value));
  }

  protected formatNullableInteger(value: number | null): string {
    if (value === null) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  protected formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(parsed);
  }

  protected formatDateTimeOrBlank(value: string): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected formatRelatedCoupon(value: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(value);
  }

  protected formatSettlementDate(value: string): string {
    if (!value) {
      return '-';
    }

    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
    if (compactMatch) {
      const [, yearText, monthText, dayText] = compactMatch;
      const year = Number(yearText);
      const month = Number(monthText);
      const day = Number(dayText);
      const parsedCompact = new Date(year, month - 1, day);

      if (
        !Number.isNaN(parsedCompact.getTime())
        && parsedCompact.getFullYear() === year
        && parsedCompact.getMonth() === month - 1
        && parsedCompact.getDate() === day
      ) {
        return new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).format(parsedCompact);
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  private isRepurcMarket(): boolean {
    const marketType = this.detail()?.marketType.trim().toUpperCase() ?? '';
    return marketType === 'REPURC';
  }

  private normalizeRepurcNumber(value: string): string {
    const trimmed = value.trim();
    const isNegative = trimmed.startsWith('-');
    const unsigned = trimmed.replace(/[^\d.]/g, '');
    const decimalIndex = unsigned.indexOf('.');
    const integerDigits = (decimalIndex >= 0 ? unsigned.slice(0, decimalIndex) : unsigned).replace(/\D/g, '');
    const fractionDigits = decimalIndex >= 0
      ? unsigned.slice(decimalIndex + 1).replace(/\D/g, '')
      : '';

    if (!integerDigits && !fractionDigits) {
      return '';
    }

    const integerPart = integerDigits || '0';
    return `${isNegative ? '-' : ''}${integerPart}${decimalIndex >= 0 ? `.${fractionDigits}` : ''}`;
  }

  private restoreRepurcInputCaret(
    input: HTMLInputElement,
    digitsBeforeCaret: number,
    fractionDigitsBeforeCaret: number,
    caretIsInFraction: boolean,
  ): void {
    let nextCaret = input.value.length;
    const decimalIndex = input.value.indexOf('.');

    if (caretIsInFraction && decimalIndex >= 0) {
      nextCaret = Math.min(decimalIndex + 1 + fractionDigitsBeforeCaret, input.value.length);
    } else {
      let digitsSeen = 0;
      nextCaret = decimalIndex >= 0 ? decimalIndex : input.value.length;
      for (let index = 0; index < input.value.length; index += 1) {
        if (/\d/.test(input.value[index])) {
          digitsSeen += 1;
        }
        if (digitsSeen >= digitsBeforeCaret) {
          nextCaret = index + 1;
          break;
        }
      }
    }

    input.setSelectionRange(nextCaret, nextCaret);
  }

  private isSectionVisible(sectionId: BondDetailSectionId): boolean {
    switch (sectionId) {
      case 'bond-setup':
        return !(this.isCdnSecurity() && !this.isRepurcMarket());
      case 'legacy':
        return Boolean(this.detail()?.isSale) && !this.isCdnSecurity();
      case 'related-instruments':
        return true;
      case 'notes':
        return true;
      case 'pricing-supplements':
        return !this.isCdnSecurity();
      case 'bond-fees':
        return this.shouldShowBondFeesSection();
      case 'investors-allocations':
        return this.shouldShowInvestorsSection();
      case 'leads-co-leads':
        return this.shouldShowLeadsCoLeadsSection();
      case 'non-named-dealers':
        return this.shouldShowNonNamedDealersSection();
      case 'documentation':
        return this.shouldShowDocumentationSection();
      default:
        return false;
    }
  }

  private loadDetail(): void {
    if (!this.tradeId) {
      this.errorMessage.set('Trade ID is missing from the route.');
      this.detail.set(null);
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.initializeError.set('');
    this.detailSubscription?.unsubscribe();
    this.relatedInstrumentsSubscription?.unsubscribe();

    this.detailSubscription = this.bondDetailService.getBondDetail(this.tradeId).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.loadNotes();
        this.loadRelatedInstruments(detail.tradeId, detail.structureId);
        this.updateDocumentTitle(detail.dealId);
        this.hasUnsavedChanges.set(false);
        this.autoUs3MlCostChecked.set(false);
        this.autoSofrCostChecked.set(false);
        this.repurcOriginalCostBeforeAuto.set(null);
        this.bpsPerYear.set('');
        this.saveInfo.set('');
        this.isLoading.set(false);

        if (!detail.isNew) {
          this.queueTabsInitialization();
        }
      },
      error: () => {
        this.detail.set(null);
        this.isLoadingRelatedInstruments.set(false);
        this.relatedInstrumentsError.set('');
        this.hasUnsavedChanges.set(false);
        this.isLoading.set(false);
        this.errorMessage.set('Unable to load bond detail data from the API.');
      },
    });
  }

  private loadRelatedInstruments(tradeId: string, structureId: string): void {
    const normalizedTradeId = tradeId.trim();
    const normalizedStructureId = structureId.trim();
    if (!normalizedTradeId || !normalizedStructureId) {
      this.detail.update((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          relatedInstruments: [],
        };
      });
      this.isLoadingRelatedInstruments.set(false);
      this.relatedInstrumentsError.set('');
      this.relatedInstrumentsDatatableController.initialize();
      return;
    }

    this.isLoadingRelatedInstruments.set(true);
    this.relatedInstrumentsError.set('');
    this.relatedInstrumentsSubscription?.unsubscribe();

    this.relatedInstrumentsSubscription = this.bondDetailService.getRelatedInstruments(normalizedTradeId, normalizedStructureId).subscribe({
      next: (rows) => {
        this.detail.update((current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            relatedInstruments: rows,
          };
        });
        this.isLoadingRelatedInstruments.set(false);
        this.relatedInstrumentsDatatableController.initialize();
      },
      error: () => {
        this.isLoadingRelatedInstruments.set(false);
        this.relatedInstrumentsError.set('Unable to load related instruments.');
        this.relatedInstrumentsDatatableController.initialize();
      },
    });
  }

  private updateDocumentTitle(dealId?: string): void {
    const normalizedDealId = (dealId ?? '').trim();
    if (normalizedDealId) {
      this.titleService.setTitle(`FRIDA - ${normalizedDealId}`);
      return;
    }

    const normalizedTradeId = this.tradeId.trim();
    this.titleService.setTitle(normalizedTradeId ? `FRIDA - ${normalizedTradeId}` : 'FRIDA');
  }

  private loadAvailableInvestors(): void {
    this.isLoadingAvailableInvestors.set(true);
    this.availableInvestorsSubscription?.unsubscribe();

    this.availableInvestorsSubscription = this.investorService.getInvestors().subscribe({
      next: (investors) => {
        this.availableInvestors.set(investors);
        this.isLoadingAvailableInvestors.set(false);
      },
      error: () => {
        this.availableInvestors.set([]);
        this.addInvestorsError.set('Unable to load investors. Please try again.');
        this.isLoadingAvailableInvestors.set(false);
      },
    });
  }

  private loadInvestorCreateLookups(): void {
    this.isLoadingInvestorCreateLookups.set(true);
    this.investorCreateLookupError.set('');
    this.investorCreationLookupSubscription?.unsubscribe();

    this.investorCreationLookupSubscription = forkJoin({
      investorTypes: this.investorService.getInvestorTypes(),
      investorGroups: this.investorService.getInvestorGroups(),
      countries: this.catalogLocationService.getCountries(),
      regions: this.catalogLocationService.getRegions(),
    }).subscribe({
      next: ({ investorTypes, investorGroups, countries, regions }) => {
        this.investorModalInvestorTypes.set(investorTypes);
        this.investorModalInvestorGroups.set(investorGroups);
        this.investorModalCountries.set(countries);
        this.investorModalRegions.set(regions);

        this.createInvestorForm.update((current) => ({
          ...current,
          regionId: this.resolveRegionIdForCountry(current.countryId),
        }));
        this.createInvestorGroupForm.update((current) => ({
          ...current,
          regionId: this.resolveRegionIdForCountry(current.hqCountryId),
        }));

        this.isLoadingInvestorCreateLookups.set(false);
        this.initializeInvestorModalSelects();
      },
      error: (error: unknown) => {
        this.isLoadingInvestorCreateLookups.set(false);
        this.investorCreateLookupError.set(resolveApiErrorMessage('Unable to load lookup data for investor creation.', error));
      },
    });
  }

  private refreshInvestorGroupsLookup(preferredGroupName: string): void {
    this.investorService.getInvestorGroups().subscribe({
      next: (groups) => {
        this.investorModalInvestorGroups.set(groups);
        const matched = groups.find((entry) => entry.name.trim().toUpperCase() === preferredGroupName.trim().toUpperCase());
        if (!matched) {
          return;
        }

        this.createInvestorForm.update((current) => ({
          ...current,
          investorGroupId: String(matched.id),
        }));
        this.initializeInvestorModalSelects();
      },
      error: () => {
        this.investorCreateLookupError.set('Unable to refresh investor groups. Please reload lookup data.');
      },
    });
  }

  private loadAvailableReofferBmks(): void {
    this.isLoadingAvailableReofferBmks.set(true);
    this.availableReofferBmksSubscription?.unsubscribe();

    this.availableReofferBmksSubscription = this.reofferBmkService.getReofferBmks().subscribe({
      next: (reoffers) => {
        this.availableReofferBmks.set(reoffers);
        this.isLoadingAvailableReofferBmks.set(false);

        const selectedId = this.selectedReofferBmkId();
        if (selectedId === null) {
          return;
        }

        const selectedReoffer = reoffers.find((entry) => entry.id === selectedId);
        if (selectedReoffer) {
          this.selectedReofferBmkName.set(selectedReoffer.name);
        }
      },
      error: () => {
        this.availableReofferBmks.set([]);
        this.isLoadingAvailableReofferBmks.set(false);
        this.addFormError.set('Unable to load reoffer list. Please try again.');
      },
    });
  }

  private loadGroupedCustomers(searchTerm = ''): void {
    this.isLoadingGroupedCustomers.set(true);
    this.appliedCustomerSearchTerm.set(searchTerm.trim());
    this.groupedCustomersSubscription?.unsubscribe();

    this.groupedCustomersSubscription = this.bondDetailService.getGroupedCustomers(searchTerm).subscribe({
      next: (groups) => {
        this.groupedCustomers.set(groups);
        this.isLoadingGroupedCustomers.set(false);
      },
      error: () => {
        this.groupedCustomers.set([]);
        this.isLoadingGroupedCustomers.set(false);
        this.addFormError.set('Unable to load customers. Please try again.');
      },
    });
  }

  private normalizeUiLeadRole(value: string | null | undefined): string {
    const normalized = (value ?? '').trim().toUpperCase().replaceAll('_', '-').replaceAll(' ', '');

    if (!normalized) {
      return '';
    }

    if (normalized === 'COLEAD' || normalized === 'CO-LEAD') {
      return 'CO-LEAD';
    }

    return 'LEAD';
  }

  private toApiLeadRole(value: string): string | null {
    const role = this.normalizeUiLeadRole(value);
    if (!role) {
      return null;
    }

    return role === 'CO-LEAD' ? 'CoLead' : 'Lead';
  }

  private calculateLeadFeeFromPercentage(): void {
    const detail = this.detail();
    const feeAmount = detail?.issFee;
    if (feeAmount === null || feeAmount === undefined || !Number.isFinite(feeAmount)) {
      return;
    }

    const percentage = this.parseNullableNumber(this.addLeadForm().feePercentage);
    if (percentage === null) {
      return;
    }

    const calculatedFee = Math.round(feeAmount * (percentage / 100));
    this.addLeadForm.update((current) => ({
      ...current,
      feeLeadColead: String(calculatedFee),
    }));
  }

  private calculateLeadPercentageFromFee(feeLeadColead: number | null): string {
    const feeAmount = this.detail()?.issFee;
    if (feeLeadColead === null || feeAmount === null || feeAmount === undefined || feeAmount === 0) {
      return '';
    }

    return String(Math.round((100 * feeLeadColead) / feeAmount));
  }

  private syncLeadPercentageFromFee(): void {
    const currentFee = this.parseNullableNumber(this.addLeadForm().feeLeadColead);
    const feePercentage = this.calculateLeadPercentageFromFee(currentFee);

    this.addLeadForm.update((current) => ({
      ...current,
      feePercentage,
    }));
  }

  private leadRowKey(row: TradeLeadItem): string {
    return `${row.tradeId}|${row.customerId}|${row.role}`;
  }

  private updateTradeLeadInDetail(customerId: string, row: TradeLeadItem): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        tradeLead: current.tradeLead.map((item) => (item.customerId === customerId ? row : item)),
      };
    });
  }

  private upsertTradeLeadInDetail(row: TradeLeadItem): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const existingIndex = current.tradeLead.findIndex((item) => item.customerId === row.customerId);
      if (existingIndex === -1) {
        return {
          ...current,
          tradeLead: [...current.tradeLead, row],
        };
      }

      return {
        ...current,
        tradeLead: current.tradeLead.map((item, index) => (index === existingIndex ? row : item)),
      };
    });
  }

  private removeTradeLeadFromDetail(customerId: string): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        tradeLead: current.tradeLead.filter((item) => item.customerId !== customerId),
      };
    });
  }

  protected prepareNonNamedDealersModal(): void {
    const current = this.detail()?.nonNamedDealers ?? [];
    this.selectedNonNamedDealerIds.set(new Set(current.map((item) => item.id)));
    this.nonNamedDealerSearchTerm.set('');
    this.nonNamedDealerFormError.set('');
    this.loadAvailableNonNamedDealers();
  }

  protected resetNonNamedDealersModal(): void {
    this.nonNamedDealerSearchTerm.set('');
    this.nonNamedDealerFormError.set('');
    this.selectedNonNamedDealerIds.set(new Set<number>());
  }

  protected isNonNamedDealerSelected(id: number): boolean {
    return this.selectedNonNamedDealerIds().has(id);
  }

  protected toggleNonNamedDealer(id: number): void {
    this.selectedNonNamedDealerIds.update((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    this.nonNamedDealerFormError.set('');
  }

  protected areAllFilteredNonNamedDealersSelected(): boolean {
    const filtered = this.filteredNonNamedDealers();
    return filtered.length > 0 && filtered.every((dealer) => this.isNonNamedDealerSelected(dealer.id));
  }

  protected toggleAllFilteredNonNamedDealers(checked: boolean): void {
    const ids = this.filteredNonNamedDealers().map((dealer) => dealer.id);
    this.selectedNonNamedDealerIds.update((current) => {
      const next = new Set(current);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  protected saveNonNamedDealers(): void {
    const currentDetail = this.detail();
    if (!currentDetail?.tradeId || this.isSavingNonNamedDealers() || this.isLoadingNonNamedDealers()) {
      return;
    }

    const selectedIds = [...this.selectedNonNamedDealerIds()];
    const currentIds = new Set(currentDetail.nonNamedDealers.map((item) => item.id));
    const unchanged = selectedIds.length === currentIds.size && selectedIds.every((id) => currentIds.has(id));
    if (unchanged) {
      this.modalController.close(this.nonNamedDealersModalId);
      this.resetNonNamedDealersModal();
      return;
    }

    const namesById = new Map<number, string>([
      ...currentDetail.nonNamedDealers.map((item): [number, string] => [item.id, item.name]),
      ...this.availableNonNamedDealers().map((item): [number, string] => [item.id, item.name]),
    ]);

    this.isSavingNonNamedDealers.set(true);
    this.nonNamedDealerFormError.set('');
    this.nonNamedDealersSubscription?.unsubscribe();
    this.nonNamedDealersSubscription = this.bondDetailService.replaceBondDealers(currentDetail.tradeId, selectedIds).subscribe({
      next: () => {
        const nonNamedDealers: NonNamedDealerItem[] = selectedIds
          .map((id) => ({ id, name: namesById.get(id) ?? '' }))
          .sort((left, right) => left.name.localeCompare(right.name));
        this.detail.update((current) => (current ? { ...current, nonNamedDealers } : current));
        this.isSavingNonNamedDealers.set(false);
        this.modalController.close(this.nonNamedDealersModalId);
        this.resetNonNamedDealersModal();
        window.KTToast?.show({ message: 'Non named dealers saved successfully.', variant: 'success' });
      },
      error: (error: unknown) => {
        this.isSavingNonNamedDealers.set(false);
        this.nonNamedDealerFormError.set(resolveApiErrorMessage('Unable to save non named dealers. Please try again.', error));
      },
    });
  }

  private loadAvailableNonNamedDealers(): void {
    this.isLoadingNonNamedDealers.set(true);
    this.availableNonNamedDealersSubscription?.unsubscribe();
    this.availableNonNamedDealersSubscription = this.nonNamedDealerService.getNonNamedDealers().subscribe({
      next: (dealers) => {
        this.availableNonNamedDealers.set(dealers);
        this.isLoadingNonNamedDealers.set(false);
      },
      error: (error: unknown) => {
        this.availableNonNamedDealers.set([]);
        this.isLoadingNonNamedDealers.set(false);
        this.nonNamedDealerFormError.set(resolveApiErrorMessage('Unable to load non named dealers. Please try again.', error));
      },
    });
  }

  private parseNullableNumber(value: string): number | null {
    if (!value.trim()) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseBmkValue(value: string): number | null {
    const normalized = value.replaceAll(',', '').trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private addBondTradeBmkToDetail(reofferId: number, reofferName: string, value: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const exists = current.bondTradeBmks.some((entry) => entry.id === reofferId);
      if (exists) {
        return {
          ...current,
          bondTradeBmks: current.bondTradeBmks.map((entry) => (
            entry.id === reofferId
              ? { ...entry, benchmark: reofferName, value: String(value) }
              : entry
          )),
        };
      }

      return {
        ...current,
        bondTradeBmks: [
          ...current.bondTradeBmks,
          {
            id: reofferId,
            benchmark: reofferName,
            tenor: '',
            value: String(value),
          },
        ],
      };
    });
  }

  private updateBondTradeBmkInDetail(reofferId: number, value: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        bondTradeBmks: current.bondTradeBmks.map((entry) => (
          entry.id === reofferId
            ? { ...entry, value: String(value) }
            : entry
        )),
      };
    });
  }

  private removeBondTradeBmkFromDetail(reofferId: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        bondTradeBmks: current.bondTradeBmks.filter((entry) => entry.id !== reofferId),
      };
    });
  }

  private buildNotePayload(description: string): NoteCreateRequest | null {
    const parentId = this.tradeId.trim();
    if (!parentId) {
      return null;
    }

    const modifiedBy = (this.detail()?.doneUser ?? '').trim() || 'FRIDA UI';

    return {
      parentId: parentId.slice(0, 20),
      source: this.notesSource,
      description,
      modifiedBy: modifiedBy.slice(0, 50),
    };
  }

  private resolveDocumentModifiedBy(): string {
    return (this.detail()?.doneUser ?? '').trim() || 'FRIDA UI';
  }

  private extractFileExtension(filename: string): string | null {
    const normalized = filename.trim();
    const extensionIndex = normalized.lastIndexOf('.');
    if (extensionIndex <= 0 || extensionIndex === normalized.length - 1) {
      return null;
    }

    return normalized.slice(extensionIndex).toLowerCase();
  }

  private isAllowedDocumentType(extension: string): boolean {
    return this.allowedDocumentTypes.includes(extension);
  }

  private generateDocumentCode(length = 12): string {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);

    return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
  }

  private loadDocumentsAttached(tradeId: string): void {
    this.documentsSubscription?.unsubscribe();
    this.documentsSubscription = this.bondDetailService.getDocumentsByTrade(tradeId).subscribe({
      next: (documents) => {
        this.detail.update((current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            documentsAttached: documents,
          };
        });
      },
      error: () => {
        // Preserve existing rows if documents endpoint refresh fails.
      },
    });
  }

  private loadNotes(): void {
    if (!this.tradeId) {
      return;
    }

    const tradeId = this.tradeId.trim().toUpperCase();
    this.notesSubscription?.unsubscribe();

    this.notesSubscription = this.bondDetailService.getNotes().subscribe({
      next: (notes) => {
        const mapped = notes
          .filter((note) => note.parentId.trim().toUpperCase() === tradeId)
          .map((note) => this.toBondNoteItem(note));

        this.detail.update((current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            notes: mapped,
          };
        });
      },
      error: () => {
        // Keep existing notes from bond detail payload if notes endpoint fails.
      },
    });
  }

  private toBondNoteItem(note: NoteResponse): BondNoteItem {
    return {
      id: note.id,
      note: note.description,
      author: note.modifiedBy,
      createdAt: note.dateModified,
    };
  }

  private upsertNoteInDetail(note: BondNoteItem): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const exists = current.notes.some((entry) => entry.id === note.id);
      if (!exists) {
        return {
          ...current,
          notes: [...current.notes, note],
        };
      }

      return {
        ...current,
        notes: current.notes.map((entry) => (entry.id === note.id ? note : entry)),
      };
    });
  }

  private updateNoteInDetail(noteId: number, noteText: string, modifiedBy: string): void {
    const now = new Date().toISOString();

    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        notes: current.notes.map((entry) => {
          if (entry.id !== noteId) {
            return entry;
          }

          return {
            ...entry,
            note: noteText,
            author: modifiedBy,
            createdAt: now,
          };
        }),
      };
    });
  }

  private removeNoteFromDetail(noteId: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        notes: current.notes.filter((entry) => entry.id !== noteId),
      };
    });
  }

  private queueTabsInitialization(): void {
    requestAnimationFrame(() => {
      this.initializeKTTabs();
    });
  }

  private formatIndexWithSpread(indexName: string, spread: string): string {
    const normalizedIndex = indexName.trim() || '-';
    const normalizedSpread = spread.trim();

    if (!normalizedSpread) {
      return normalizedIndex;
    }

    if (/bps$/i.test(normalizedSpread)) {
      return `${normalizedIndex} ${normalizedSpread}`;
    }

    if (normalizedSpread.startsWith('+') || normalizedSpread.startsWith('-')) {
      return `${normalizedIndex} ${normalizedSpread}bps`;
    }

    const numericMatch = normalizedSpread.match(/-?\d+(\.\d+)?/);
    if (!numericMatch) {
      return `${normalizedIndex} ${normalizedSpread}`;
    }

    const parsed = Number(numericMatch[0]);
    if (!Number.isFinite(parsed) || parsed === 0) {
      return `${normalizedIndex} ${normalizedSpread}`;
    }

    return `${normalizedIndex} ${parsed > 0 ? '+' : ''}${normalizedSpread}bps`;
  }

  private initializeKTTabs(): void {
    const tabsElement = this.tabsRoot?.nativeElement;
    if (!tabsElement) {
      return;
    }

    const tabsApi = (window as Window & { KTTabs?: KTTabsConstructor }).KTTabs;
    if (!tabsApi) {
      return;
    }

    tabsApi.createInstances?.();
    this.tabsInstance = tabsApi.getOrCreateInstance?.(tabsElement) ?? null;

    const defaultTab = tabsElement.querySelector<HTMLElement>('[data-kt-tab-toggle].active');
    if (this.tabsInstance && defaultTab) {
      this.tabsInstance.show(defaultTab);
    }
  }

  private toBondDetailUpsertRequest(detail: BondDetailModel): BondDetailUpsertRequest {
    return {
      tradeId: detail.tradeId,
      bondTypeId: detail.bondTypeId,
      bondProceedId: detail.bondProceedId,
      bondDocumentationId: detail.bondDocumentationId,
      secId: detail.secId,
      bondGlRepurchase: detail.bondGlRepurchase,
      bondGlRefinance: detail.bondGlRefinance,
      us3MlCost: detail.us3MlCost,
      sofrCost: detail.sofrCost,
      repurcOriginalCost: detail.repurcOriginalCost,
      repurcNewFundingCost: detail.repurcNewFundingCost,
      comments: detail.comments,
      bondTheoreticalFee: detail.bondTheoreticalFee,
      genericField1: detail.genericField1,
      genericField2: detail.genericField2,
      genericField3: detail.genericField3,
      tranche: detail.tranche,
      hasLegalDocument: detail.hasLegalDocument,
      hasTermSheet: detail.hasTermSheet,
      hasRTA: detail.hasRta,
      executedDocsSignatureRemoved: detail.executedDocsSignatureRemoved,
      isPrivatePricingSupplement: detail.isPrivatePricingSupplement,
    };
  }
}
